import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { AuthContext } from "../../context/AuthContext";
import EngagementDashboard from "./EngagementDashboard";
import PainPointsPanel from "../../components/customers/PainPointsPanel";
import { ContainerCard } from "../../components/ui/cards";
import { cn } from "../../utils/tailwindUtils";
import { useLocation, useNavigate } from "react-router-dom";
import {
    Users, FileText, Calendar, Plus, X, Edit,
    Trash, MapPin, Clock, Package, ArrowRight, CheckCircle, TrendingUp, Check,
    Edit3, Phone, Briefcase, MessageSquare, XCircle, Send, Handshake, Trophy,
    Pause, User, IndianRupee, AlertTriangle, Sparkles, Mail, CheckSquare, BadgeCheck,
    FolderKanban,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input, Textarea } from "../../components/ui/input";
import { DialogModal } from "../../components/ui/dialogModal";
import { Skeleton } from "../../components/ui/skeleton";
import { DatePicker, Select } from "antd";
import useApiMethods from "../../lib/useApiMethods";
import { CUSTOMERDETAILSUPDATE, CUSTOMERLOG, CUSTOMERLOG_EDIT, CUSTOMERSLIST, TASKS_URL, USERS_SIMPLE_URL, EMAIL_MESSAGES_URL, EMAIL_ACCOUNTS_URL, EMAIL_SEND_URL, EMAIL_TEMPLATES_URL, WA_MESSAGES_URL, WA_ACCOUNTS_URL, WA_TEMPLATES_URL, WA_SEND_URL, CUSTOMER_TYPE_MASTER_URL, QUOTATIONS_URL, ONBOARDED_URL, MOU_URL, ASSIGNEDTOUSER, CHANNEL_PARTNER_OPTIONS_URL, CRE_HANDOVER_URL, PROJECTS_URL, PROJECT_ASSIGNMENT_CONFIG_URL, STAGE_REQUIRED_FIELDS_CONFIG_URL } from "../../constants/api";
import AddOnboardedModal from "../onboarded/AddOnboardedModal";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import ToastMsg from "../../components/common/ToastMsg";
import LeadAISuggestionPanel from "../../components/customers/LeadAISuggestionPanel";
import CustomerAttributesPanel from "../../components/customers/CustomerAttributesPanel";
import HospitalProfilePanel from "../../components/customers/HospitalProfilePanel";
import CREHandoverPanel from "../../components/customers/CREHandoverPanel";
import LeadEnquiriesPanel, { LeadReturnBanner } from "../../components/customers/LeadEnquiriesPanel";
import PartnerSourcedLeadsPanel from "../../components/customers/PartnerSourcedLeadsPanel";
import ReferralBenefitsPanel from "../../components/customers/ReferralBenefitsPanel";
import { CUSTOMER_REFERRAL_REWARDS_URL } from "../../constants/api";
import CloseWonValueModal from "../../components/customers/CloseWonValueModal";
import OwnerHistory from "../../components/assignment/OwnerHistory";
import {
    CustomerHeader, MobileActionBar, NextFollowUpCard, QuickActions, StageProgress, TabNav,
} from "../../components/customers/detail/CustomerDetailParts";
import EmailReader from "../../components/customers/detail/EmailReader";
import EmailsTab from "../../components/customers/detail/EmailsTab";
import { groupThreads } from "../../components/customers/detail/emailThreads";
import { useContactVerification, ContactVerify } from "../../components/customers/detail/contactVerification";
import DemoSlotsCard from "../../components/customers/detail/DemoSlotsCard";
import DemoFeedbackCard from "../../components/customers/detail/DemoFeedbackCard";
import DemoReminderCard from "../../components/customers/detail/DemoReminderCard";
import NurtureCard from "../../components/customers/detail/NurtureCard";
import DemoPresenterField from "../../components/customers/detail/DemoPresenterField";
import CompanyResearchPanel from "../../components/customers/detail/CompanyResearchPanel";
import LeadStatusChips from "../../components/customers/detail/LeadStatusChips";
import { EMAIL_CUSTOMER_SYNC_URL } from "../../constants/api";
import CallButton from "../../components/telephony/CallButton";
import CallHistory from "../../components/telephony/CallHistory";
import dayjs from 'dayjs';
import LocationSelects from '../../components/common/LocationSelects';
import { DEFAULT_COUNTRY, stateCode, stateName } from '../../utils/location';
import { formatDateTime as fmtDateTime } from '../../utils/datetime';

// Must match CustomerDetails.CHANNEL_PARTNER_SOURCE on the backend — the one
// lead_source value that carries a partner alongside it.
const CHANNEL_PARTNER_SOURCE = 'Channel Partner';

const LEAD_SOURCE_OPTIONS = [
    'Meta', 'Website', 'Instagram', 'Linked In', 'Whats app', 'Referal', 'Lusha',
    'Direct Visit', CHANNEL_PARTNER_SOURCE,
].map(v => ({ value: v, label: v }));

// Tolerant of the spellings years of imports left behind ('Channel Partner ',
// lower case): the backend compares the same way, so the picker must appear on
// exactly the leads whose attribution the API will accept.
const isPartnerSource = (v) => (v || '').trim().toLowerCase() === CHANNEL_PARTNER_SOURCE.toLowerCase();

// Stages that require a stall reason when transitioning in (mirrors backend)
const STALL_REASON_STAGES = ['On Hold', 'Closed Lost', 'Dis-Qualified', 'Nurture Lead'];
// Moves that name who takes the demo ("Demo by").
const DEMO_PRESENTER_STAGES = ['Demo Fixed', 'Demo Done'];

/* Stages with nothing left to follow up. Everywhere else a next follow-up date
   is required — No Show and On Hold included, since a stalled lead is exactly
   the one that needs its next step booked before the rep moves on. Mirrors
   LeadStatusLog.NO_FOLLOWUP_STAGES on the server, which is what actually
   enforces it. Nurture needs one too: it is the date to revisit the lead. */
const NO_FOLLOWUP_STAGES = ['Closed Won', 'Closed Lost', 'Dis-Qualified', 'Onboarded'];

const STALL_REASON_OPTIONS = [
    { value: 'budget',         label: 'Budget' },
    { value: 'timing',         label: 'Timing' },
    { value: 'decision_maker', label: 'Decision Maker' },
    { value: 'competitor',     label: 'Competitor' },
    { value: 'no_response',    label: 'No Response' },
    { value: 'contract_running', label: 'Using Another System' },
    { value: 'project_later',  label: 'Project Later' },
    { value: 'other',          label: 'Other' },
];

/* Tabs that show the activity timeline under their own content. An allow-list,
   not a deny-list: the deny-list missed Hospital Profile and Referral Benefits,
   which then printed the whole timeline under their panels. */
const TIMELINE_TABS = ['All Activities', 'CRM Events', 'Notes', 'Calls', 'Documents'];

const CHANNEL_OPTIONS = [
    { value: 'call',     label: 'Call' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'email',    label: 'Email' },
    { value: 'visit',    label: 'Visit' },
    { value: 'demo',     label: 'Demo' },
];

const CONTACT_OUTCOME_OPTIONS = [
    { value: 'connected',          label: 'Connected' },
    { value: 'no_answer',          label: 'No Answer' },
    { value: 'busy',               label: 'Busy' },
    { value: 'wrong_number',       label: 'Wrong Number' },
    { value: 'callback_requested', label: 'Callback Requested' },
];

const CustomerDetails = () => {
    const { hasFeature, switchProject, hasPermission } = useContext(AuthContext);
    const [isEditMode, setIsEditMode] = useState(false);
    const [formData, setFormData] = useState({});
    const { create, fetch, update, remove } = useApiMethods()
    const [showAddContact, setShowAddContact] = useState(false);
    const [editingContact, setEditingContact] = useState(null);
    const [customerDetails, setCustomerDetails] = useState({});
    const customerData = customerDetails;
    const [partnerOptions, setPartnerOptions] = useState([]);
    // Organisation-wide switch (Settings → Projects). When on, the info card
    // gets a Project picker; the options are the projects this user can open.
    const [projectAssignOn, setProjectAssignOn] = useState(false);
    const [projectOptions, setProjectOptions] = useState([]);

    const [newContact, setNewContact] = useState({
        name: "",
        designation: "",
        contact_number: "",
        email: "",
        type: "Secondary Contact",
        role: ""
    });

    const [statusForm, setStatusForm] = useState({
        from_status: customerData.lead_stage || 'New Lead', // Initialize with current status
        to_status: null,
        remarks: "",
        next_followup_date: null,
        demo_datetime: null,
        stall_reason: null,
        channel: null,
        contact_outcome: null
    });

    const [loading, setLoading] = useState(false)
    const location = useLocation();
    // The open tab lives in the URL (?tab=Notes) so a link can open one, and
    // the back button and a reload keep it.
    const [activeTab, setActiveTab] = useState(
        () => new URLSearchParams(location.search).get('tab') || 'All Activities');
    // Update stage / follow-up: the status form, in a modal so it can open
    // from the header, the stepper and the follow-up card.
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
    const workspaceRef = useRef(null);
    const detailsRef = useRef(null);
    // Staff click-to-call: reload the call list once the phones have had a
    // moment to ring, so the new row shows its status rather than "initiated".
    const [callRefreshKey, setCallRefreshKey] = useState(0);
    const onStaffCalled = () => setTimeout(() => setCallRefreshKey(k => k + 1), 3000);
    const [editingCard, setEditingCard] = useState(null); // 'info' | 'deal' | null
    const [showDealHistory, setShowDealHistory] = useState(false);
    const [noteText, setNoteText] = useState('');
    const [savingNote, setSavingNote] = useState(false);
    const [editingNoteId, setEditingNoteId] = useState(null);
    const [editingNoteText, setEditingNoteText] = useState('');

    // Onboard modal
    const [showOnboardModal, setShowOnboardModal] = useState(false);

    // Frozen quotation (if any) for this customer
    const [frozenQuote, setFrozenQuote] = useState(null);

    // The CRE handover, fetched up front because the tab only exists when the
    // tenant collects one. Held here rather than in the panel so the tab list
    // and the panel agree without asking the server the same thing twice.
    const [handover, setHandover] = useState(null);

    /* What the handover still needs before this deal can be won. The server
       refuses the transition on exactly this list, so this copy exists to save
       the rep the round trip and to name the fields while the status form is
       still open — it is not what enforces the rule. Empty for a tenant that
       does not collect a handover. */
    const handoverBlockers = useMemo(
        () => (handover?.is_enabled ? (handover.required_missing || []) : []),
        [handover]);

    /* Lead fields an admin has made mandatory per stage (Settings → Mandatory
       Fields). When the stage picked on the status form needs one this lead
       does not have, the form asks for it in place and sends it with the
       status, so the rep fills State and moves the stage in one save. The
       server enforces the same rule; this copy is so the rep sees it first. */
    const [stageFieldRules, setStageFieldRules] = useState([]);
    const [leadFieldValues, setLeadFieldValues] = useState({});
    const loadStageFieldRules = () => {
        fetch(STAGE_REQUIRED_FIELDS_CONFIG_URL).then(res => {
            if (res?.status === 'success') setStageFieldRules(res.fields || []);
        }).catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { loadStageFieldRules(); }, []);

    const stageFieldsMissing = useMemo(() => {
        const to = statusForm.to_status;
        // Only a real move is gated — same rule as the server.
        if (!to || to === customerDetails.lead_stage) return [];
        return stageFieldRules.filter(f =>
            (f.stages || []).includes(to) && !String(customerDetails[f.key] ?? '').trim());
    }, [stageFieldRules, statusForm.to_status, customerDetails]);

    /* The "needed for this stage" box on both status forms. A function, not a
       const, because it reads option lists declared further down. */
    const renderStageFieldsBox = () => {
        if (!stageFieldsMissing.length) return null;
        const keys = stageFieldsMissing.map(f => f.key);
        const setVal = (k, v) => setLeadFieldValues(prev => ({ ...prev, [k]: v }));
        const country = leadFieldValues.country || customerDetails.country || DEFAULT_COUNTRY;
        // City needs a state to list cities from, and state needs a country,
        // so a missing child brings its blank parents along as pickers.
        const locKeys = ['country', 'state', 'city'];
        const needLoc = locKeys.filter(k => keys.includes(k));
        const deepest = Math.max(-1, ...needLoc.map(k => locKeys.indexOf(k)));
        const showLoc = locKeys.filter((k, i) => i <= deepest
            && (keys.includes(k) || !String(customerDetails[k] ?? '').trim()));
        const inputCls = "w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm";
        const lbl = (text) => (
            <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">
                {text}<span className="text-red-500 ml-1">*</span>
            </label>
        );
        return (
            <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/10 dark:border-amber-700 p-3 space-y-3">
                <p className="text-xs text-amber-800 dark:text-amber-300">
                    <strong>{statusForm.to_status}</strong> needs{' '}
                    {stageFieldsMissing.map(f => f.label).join(', ')} on this lead. Fill{' '}
                    {stageFieldsMissing.length === 1 ? 'it' : 'them'} here and they are saved with the status.
                </p>
                {showLoc.length > 0 && (
                    <LocationSelects
                        show={showLoc} required={needLoc} columns={Math.min(showLoc.length, 3)}
                        labelClassName="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block"
                        value={{
                            country,
                            state: leadFieldValues.state || stateName(country, customerDetails.state),
                            city: leadFieldValues.city || customerDetails.city || '',
                        }}
                        onChange={loc => setLeadFieldValues(prev => ({ ...prev, ...loc }))} />
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {keys.includes('contact_name') && (
                        <div>{lbl('Contact name')}
                            <Input value={leadFieldValues.contact_name || ''} placeholder="Primary contact's name"
                                onChange={e => setVal('contact_name', e.target.value)} className={inputCls} />
                        </div>
                    )}
                    {keys.includes('contact_number') && (
                        <div>{lbl('Contact number')}
                            <Input value={leadFieldValues.contact_number || ''} placeholder="Phone number" inputMode="tel"
                                onChange={e => setVal('contact_number', e.target.value)} className={inputCls} />
                        </div>
                    )}
                    {keys.includes('lead_source') && (
                        <div>{lbl('Lead source')}
                            <Select size="large" className="w-full" value={leadFieldValues.lead_source || undefined}
                                placeholder="Where did this lead come from?" options={LEAD_SOURCE_OPTIONS}
                                onChange={v => setVal('lead_source', v)} />
                        </div>
                    )}
                    {keys.includes('customer_type') && (
                        <div>{lbl('Customer type')}
                            <Select size="large" className="w-full" value={leadFieldValues.customer_type || undefined}
                                placeholder="Select customer type" options={customerTypeOptions}
                                onChange={v => setVal('customer_type', v)} />
                        </div>
                    )}
                </div>
                {keys.includes('address') && (
                    <div>{lbl('Address')}
                        <Textarea value={leadFieldValues.address || ''} rows={2} placeholder="Lead address"
                            onChange={e => setVal('address', e.target.value)} className={`${inputCls} resize-none`} />
                    </div>
                )}
            </div>
        );
    };

    /* Shown beside the deal value whenever Closed Won is selected: what the
       handover still needs, or that it is complete. The rep reads it before
       pressing save rather than after being refused. Nothing at all for a
       tenant that does not collect a handover. */
    const handoverStatusBanner = !handover?.is_enabled ? null : (
        <div className={cn(
            "rounded-lg border px-3 py-2.5 flex items-center justify-between gap-3 text-sm",
            handoverBlockers.length
                ? "border-amber-300 bg-amber-50 dark:bg-amber-900/10"
                : "border-emerald-300 bg-emerald-50/60 dark:bg-emerald-900/15",
        )}>
            <span className="min-w-0">
                {handoverBlockers.length ? (
                    <span className="text-amber-800 dark:text-amber-300">
                        CRE handover incomplete — {handoverBlockers.length} field
                        {handoverBlockers.length === 1 ? '' : 's'} still needed:{' '}
                        <strong>{handoverBlockers.slice(0, 3).map(f => f.label).join(', ')}</strong>
                        {handoverBlockers.length > 3 && ` and ${handoverBlockers.length - 3} more`}
                    </span>
                ) : (
                    <>CRE handover complete — the implementation team has what it needs.</>
                )}
            </span>
            {handoverBlockers.length > 0 && (
                <button type="button"
                    onClick={() => { setActiveTab('CRE Handover'); }}
                    className="text-xs font-medium underline shrink-0">
                    Fill it in
                </button>
            )}
        </div>
    );

    /* Closed Won cannot be saved without a deal value. The modal collects it —
       by freezing the quotation the client agreed to, or by taking a typed
       estimate — and parks the answer here until the event is submitted. */
    const [closeWonOpen, setCloseWonOpen] = useState(false);
    const [dealValue, setDealValue] = useState(null);   // { deal_value, quotation_id, source }

    // Existing onboarded record id for this customer (null if not yet onboarded)
    const [existingOnboardedId, setExistingOnboardedId] = useState(null);
    /* Where this lead's MOU has got to, and whether it stands between the lead
       and onboarding. Read from the MOU pipeline rather than guessed: the gate
       is a per-project setting, so "can this be onboarded" is not something
       this screen can work out from the lead alone. */
    const [mouRow, setMouRow] = useState(null);
    const [mouConfig, setMouConfig] = useState(null);

    // Tasks state
    const [customerTasks, setCustomerTasks] = useState([]);
    const [taskUsers, setTaskUsers] = useState([]);
    const [reassignOpen, setReassignOpen] = useState(false);
    const [ownerHistoryOpen, setOwnerHistoryOpen] = useState(false);
    const [reassignToId, setReassignToId] = useState(null);
    const [reassignSaving, setReassignSaving] = useState(false);
    const [taskForm, setTaskForm] = useState({ title: '', assigned_to_id: null, due_date: null, priority: 'Medium' });
    const [savingTask, setSavingTask] = useState(false);

    // Emails state
    const [emails, setEmails] = useState([]);
    // Emails tab: the email open in the reading panel, and the list filters.
    const [openEmailId, setOpenEmailId] = useState(null);
    const [syncingEmail, setSyncingEmail] = useState(false);
    const [emailDir, setEmailDir] = useState('all');      // all | sent | received
    const [emailQuery, setEmailQuery] = useState('');
    const [emailAccounts, setEmailAccounts] = useState([]);
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [composeOpen, setComposeOpen] = useState(false);

    // WhatsApp state
    const [waMessages, setWaMessages] = useState([]);
    const [waAccounts, setWaAccounts] = useState([]);
    const [waTemplates, setWaTemplates] = useState([]);
    const [waText, setWaText] = useState('');
    const [waTemplate, setWaTemplate] = useState(null);
    const [waTemplateVars, setWaTemplateVars] = useState({});
    const [waSending, setWaSending] = useState(false);
    const [waPhone, setWaPhone] = useState('');
    const [sendingEmail, setSendingEmail] = useState(false);
    const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
    const [templateSearch, setTemplateSearch] = useState('');
    const [showCcBcc, setShowCcBcc] = useState(false);
    const [emailForm, setEmailForm] = useState({
        from_account_id: null, to: '', cc: '', bcc: '',
        subject: '', body_html: ''
    });
    // Compose opened from the header can beat the Emails tab's account load;
    // fill From once the mailboxes arrive.
    useEffect(() => {
        if (composeOpen && emailAccounts.length) {
            setEmailForm(p => (p.from_account_id ? p : { ...p, from_account_id: emailAccounts[0].id }));
        }
    }, [composeOpen, emailAccounts]);

    const navigate = useNavigate();

    /* The MOU stands in the way only when the project says it does. Unsigned
       and enforced -> the button goes to the MOU instead of the onboard form,
       because the API would refuse the form anyway and a rejected submission
       is a worse way to learn that than a button that says where to go. */
    const mouBlocks = Boolean(
        mouConfig?.require_mou
        && mouConfig?.block_onboarding_until_signed
        && !existingOnboardedId
        && mouRow?.status !== 'signed'
    );
    const onboardAction = () => {
        if (existingOnboardedId) { navigate(`/onboarded/${existingOnboardedId}`); return; }
        if (mouBlocks) { navigate(mouRow?.id ? `/mou/${mouRow.id}` : '/mou'); return; }
        setShowOnboardModal(true);
    };
    const onboardLabel = existingOnboardedId ? 'View Onboarded'
        : mouBlocks ? (mouRow?.id ? 'MOU pending' : 'Prepare MOU')
        : 'Onboard';
    const { row } = location.state || {};
    const queryParams = new URLSearchParams(location.search);
    const customerId = row?.id || queryParams.get('id');
    // Verified email / mobile badges on the contact cards; editors can also
    // mark a value verified by hand (contactVerification.js).
    const contactVerification = useContactVerification(customerId, customerData.contacts);
    const canVerifyContacts = hasPermission('customers', 'edit');
    // Company research is a plan feature (on for tecnospice only).
    const canResearch = hasFeature('company_research');

    const isChannelPartner = (customerData.customer_category || row?.customer_category) === 'channel_partner';

    // Whether eMedHub has awarded this customer anything for referring others.
    // Fetched here rather than inside the panel because it decides whether the
    // tab exists at all, and a tab that is always present and usually empty is
    // the thing the Sourced Leads rule above exists to avoid.
    const [hasReferralBenefits, setHasReferralBenefits] = useState(false);

    useEffect(() => {
        if (!customerId) { setHasReferralBenefits(false); return; }
        let cancelled = false;
        fetch(CUSTOMER_REFERRAL_REWARDS_URL, { customer_id: customerId })
            .then(res => {
                if (!cancelled && res?.status === 'success') {
                    setHasReferralBenefits((res.data || []).length > 0);
                }
            })
            // Silent: this only decides whether an extra tab appears, and a
            // toast about it would be noise on every ordinary customer.
            .catch(() => { });
        return () => { cancelled = true; };
    }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

    const leadStageOptions = isChannelPartner
        ? [
            { value: 'Onboarding',        label: 'Onboarding' },
            { value: 'Agreement Signed',  label: 'Agreement Signed' },
            { value: 'Active',            label: 'Active' },
            { value: 'Inactive',          label: 'Inactive' },
            { value: 'Suspended',         label: 'Suspended' },
            { value: 'Terminated',        label: 'Terminated' },
        ]
        : [
            { value: 'New Lead',      label: 'New Lead' },
            { value: 'Unanswered',    label: 'Unanswered' },
            { value: 'Contacted',     label: 'Contacted' },
            { value: 'Qualified',     label: 'Qualified' },
            { value: 'Dis-Qualified', label: 'Dis-Qualified' },
            { value: 'Nurture Lead',       label: 'Nurture Lead (real lead, not now)' },
            { value: 'Demo Fixed',    label: 'Demo Fixed' },
            { value: 'Demo Done',     label: 'Demo Done' },
            { value: 'Proposal',      label: 'Proposal' },
            { value: 'Negotiation',   label: 'Negotiation' },
            { value: 'Closed Won',    label: 'Closed Won' },
            { value: 'Closed Lost',   label: 'Closed Lost' },
            { value: 'On Hold',       label: 'On Hold' },
            { value: 'No Show',       label: 'No Show' },
        ];

    /* Load the customer. The skeleton is for the first load of a customer
       only: a refresh after a save keeps the page on screen (refreshing shows
       a small indicator instead), so the open tab, the scroll position and the
       child panels survive the save instead of remounting and refetching.
       `id` defaults to this page's customer: callers used to pass row.id, which
       is undefined when the page was opened from a link with only ?id=. */
    const [refreshing, setRefreshing] = useState(false);
    const loadedIdRef = useRef(null);
    const refetchCustomerDetails = async (id = customerId) => {
        const first = loadedIdRef.current !== String(id);
        if (first) setLoading(true); else setRefreshing(true);
        try {
            const response = await fetch(CUSTOMERSLIST, { customer_id: id });
            if (response?.status === 'success' && response.data) {
                setCustomerDetails(response.data);
                loadedIdRef.current = String(id);
            } else {
                ToastMsg('error', response?.message || 'Could not load this customer');
            }
        } catch (error) {
            ToastMsg('error', error.message || "Failed to refetch customer details");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    //fetch funnel category list

    useEffect(() => {
        if (customerId) {
            refetchCustomerDetails(customerId)
            // load tasks and users for Tasks tab
            fetch(TASKS_URL + `?customer_id=${customerId}`).then(res => {
                if (res?.status === 'success') setCustomerTasks(res.data || []);
            });
            fetch(USERS_SIMPLE_URL).then(res => {
                if (res?.status === 'success') setTaskUsers(res.data || []);
            });
            // Cheap for a tenant that does not run a handover: the endpoint
            // short-circuits to is_enabled=false without walking any fields.
            fetch(`${CRE_HANDOVER_URL}?customer_id=${customerId}`).then(res => {
                if (res?.status === 'success') setHandover(res);
            });
            // load quotations and pick the accepted (frozen) one if any
            fetch(`${QUOTATIONS_URL}?customer_id=${customerId}`).then(res => {
                if (res?.status === 'success') {
                    const accepted = (res.data || []).find(q => q.status === 'accepted');
                    setFrozenQuote(accepted || null);
                }
            });
            // check if this customer already has an onboarded record (created on freeze)
            fetch(`${ONBOARDED_URL}?customer_id=${customerId}`).then(res => {
                if (res?.status === 'success' && (res.data || []).length > 0) {
                    setExistingOnboardedId(res.data[0].id);
                } else {
                    setExistingOnboardedId(null);
                }
            });
            // where the MOU for this lead has got to, and whether it gates onboarding
            fetch(MOU_URL, { status: 'all' }).then(res => {
                if (res?.status !== 'success') return;
                setMouConfig(res.config || null);
                setMouRow((res.data || []).find(r => String(r.customer_id) === String(customerId)) || null);
            }).catch(() => {});
            // load emails on mount so All Activities timeline shows them immediately
            if (hasFeature('email_integration')) {
                fetch(`${EMAIL_MESSAGES_URL}?customer_id=${customerId}`).then(res => {
                    if (res?.status === 'success') setEmails(res.data || []);
                });
            }
            // load WA messages on mount for All Activities timeline
            if (hasFeature('whatsapp_campaigns') || hasFeature('whatsapp_inbox')) {
                fetch(`${WA_MESSAGES_URL}?customer_id=${customerId}`).then(res => {
                    if (res?.status === 'success') setWaMessages(res.data || []);
                });
            }
        }
    }, [customerId])

    // Another customer opens on the tab its link names, or on All Activities -
    // not on whatever tab the previous customer was left on.
    const lastCustomerRef = useRef(customerId);
    useEffect(() => {
        if (lastCustomerRef.current === customerId) return;
        lastCustomerRef.current = customerId;
        setActiveTab(new URLSearchParams(location.search).get('tab') || 'All Activities');
    }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Keep ?tab= in step with the open tab, without adding history entries.
    useEffect(() => {
        const qp = new URLSearchParams(location.search);
        if ((qp.get('tab') || 'All Activities') === activeTab) return;
        if (activeTab === 'All Activities') qp.delete('tab'); else qp.set('tab', activeTab);
        const search = qp.toString();
        navigate({ pathname: location.pathname, search: search ? `?${search}` : '' },
                 { replace: true, state: location.state });
    }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

    // Being sent to the handover tab (a blocker on Closed Won) closes the stage
    // modal, so the rep sees the form they were sent to.
    useEffect(() => { if (activeTab === 'CRE Handover') setStatusModalOpen(false); }, [activeTab]);

    const loadedOnceRef = useRef({ wa: false, email: false });
    // Refresh WA messages when WhatsApp tab is opened
    useEffect(() => {
        if (activeTab === 'WhatsApp' && customerId) {
            fetch(`${WA_MESSAGES_URL}?customer_id=${customerId}`).then(res => {
                if (res?.status === 'success') setWaMessages(res.data || []);
            });
            // Accounts and templates do not change while the page is open:
            // loaded on the first visit to the tab, not on every visit.
            if (!loadedOnceRef.current.wa) {
                loadedOnceRef.current.wa = true;
                fetch(WA_ACCOUNTS_URL).then(res => {
                    if (res?.status === 'success') setWaAccounts(res.data || []);
                });
                fetch(WA_TEMPLATES_URL).then(res => {
                    if (res?.status === 'success') setWaTemplates((res.data || []).filter(t => t.status === 'approved'));
                });
            }
        }
    }, [activeTab, customerId]);

    // Customer type options — loaded from master
    const [customerTypeOptions, setCustomerTypeOptions] = useState([]);
    useEffect(() => {
        fetch(CUSTOMER_TYPE_MASTER_URL).then(res => {
            if (res?.status === 'success') {
                setCustomerTypeOptions(
                    (res.data || [])
                        .filter(t => t.is_active)
                        .map(t => ({ value: t.name, label: t.name }))
                );
            }
        });
        fetch(CHANNEL_PARTNER_OPTIONS_URL, { limit: 200 }).then(res => {
            if (res?.status === 'success') {
                setPartnerOptions((res.data || []).map(p => ({ value: p.id, label: p.customer_name })));
            }
        }).catch(() => {});
        fetch(PROJECT_ASSIGNMENT_CONFIG_URL).then(res => {
            if (res?.status !== 'success' || !res.is_enabled) return;
            setProjectAssignOn(true);
            fetch(PROJECTS_URL).then(r => {
                if (r?.status === 'success') setProjectOptions((r.data || []).filter(p => p.is_active));
            });
        }).catch(() => {});
    }, []);

    // Refresh emails + accounts + templates when Emails tab is opened
    // Re-fetching emails here ensures open/click tracking status is always current
    useEffect(() => {
        if (activeTab === 'Emails' && customerId) {
            fetch(`${EMAIL_MESSAGES_URL}?customer_id=${customerId}`).then(res => {
                if (res?.status === 'success') setEmails(res.data || []);
            });
            if (!loadedOnceRef.current.email) {
                loadedOnceRef.current.email = true;
                fetch(EMAIL_ACCOUNTS_URL).then(res => {
                    if (res?.status === 'success') setEmailAccounts(res.data || []);
                });
                fetch(EMAIL_TEMPLATES_URL).then(res => {
                    if (res?.status === 'success') setEmailTemplates(res.data || []);
                });
            }
        }
    }, [activeTab, customerId]);

    // Initialize form data
    useEffect(() => {
        if (customerData) {
            setFormData({
                customer_name: customerData.customer_name || "",
                customer_type: customerData.customer_type || "",
                is_active: customerData.is_active ?? true,
                lead_source: customerData.lead_source || "",
                sourced_by_partner: customerData.sourced_by_partner || null,
                project_id: customerData.project_id || null,
                extra_project_ids: (customerData.extra_projects || []).map(p => p.id),
                // Contract Details
                address: customerData.address || "",
                country: customerData.country || DEFAULT_COUNTRY,
                state: stateName(customerData.country || DEFAULT_COUNTRY, customerData.state),
                city: customerData.city || "",
                contract_period: customerData.contract_details?.contract_period || "",
                type_of_hospital: customerData.contract_details?.type_of_hospital || "",
                number_of_beds: customerData.contract_details?.number_of_beds || "",
                contract_volume: customerData.contract_details?.contract_volume || "",
                potential: customerData.contract_details?.potential || "",
                customer_competitor: customerData.contract_details?.customer_competitor || "",
                // Technical Info
                current_software_used: customerData.technical_info?.current_software_used || "",
                server_type: customerData.technical_info?.server_type || "",
                no_of_systems: customerData.technical_info?.no_of_systems || "",
                no_of_users: customerData.technical_info?.no_of_users || "",
                internet_connectivity: customerData.technical_info?.internet_connectivity || "",
                // Hospital Capacity
                beds: customerData.hospital_capacity?.beds || "",
                opd_per_day: customerData.hospital_capacity?.opd_per_day || "",
                ip_admissions_per_day: customerData.hospital_capacity?.ip_admissions_per_day || "",
                doctors: customerData.hospital_capacity?.doctors || "",
                departments: customerData.hospital_capacity?.departments || "",
                // Financials
                proposed_value: customerData.financials?.proposed_value || "",
                discount_offered: customerData.financials?.discount_offered || "",
                final_quoted_price: customerData.financials?.final_quoted_price || "",
                expected_closing_date: customerData.financials?.expected_closing_date || "",
                // Follow-up
                last_discussion_summary: customerData.followup?.last_discussion_summary || "",
                next_followup_date: customerData.followup?.next_followup_date || "",
                recent_conversation: customerData.followup?.recent_conversation || "",
                pending_commitments: customerData.followup?.pending_commitments || "",
                remarks: customerData.followup?.remarks || "",
                // Competition Analysis
                potential_threat: customerData.competition_analysis?.potential_threat || "",
                present_objective: customerData.competition_analysis?.present_objective || "",
                long_term_objective: customerData.competition_analysis?.long_term_objective || "",
                value_addition: customerData.competition_analysis?.value_addition || "",
            });
        }
    }, [customerData]);

    // Sync status form with current lead stage whenever customer data loads/changes
    useEffect(() => {
        if (customerData.lead_stage) {
            setStatusForm(prev => ({
                ...prev,
                from_status: customerData.lead_stage,
                to_status: customerData.lead_stage,
            }));
        }
    }, [customerData.lead_stage]);

    // Add this useEffect after your other useEffects
    useEffect(() => {
        if (isEditMode) {
            const proposed = parseFloat(formData.proposed_value) || 0;
            const discount = parseFloat(formData.discount_offered) || 0;

            if (proposed > 0 && discount >= 0) {
                const discountAmount = (proposed * discount) / 100;
                const finalPrice = proposed - discountAmount;

                setFormData(prev => ({
                    ...prev,
                    final_quoted_price: finalPrice.toFixed(2)
                }));
            } else if (proposed > 0) {
                // If only proposed value is entered, set final price same as proposed
                setFormData(prev => ({
                    ...prev,
                    final_quoted_price: proposed.toFixed(2)
                }));
            } else {
                // Reset final price if no proposed value
                setFormData(prev => ({
                    ...prev,
                    final_quoted_price: ""
                }));
            }
        }
    }, [formData.proposed_value, formData.discount_offered, isEditMode]);

    const handleBack = () => {
        navigate(-1);
    };

    //Funnel Category Handler
    // Status Update Handler with proper date handling
    const handleStatusUpdate = async () => {
        try {
            if (!statusForm.from_status && customerData?.id && statusForm.remarks && statusForm.next_followup_date) {
                ToastMsg('error', 'Please select something');
                return;
            }

            if (!statusForm.remarks?.trim()) {
                ToastMsg('error', 'Please write remarks for this activity');
                return;
            }

            if (!NO_FOLLOWUP_STAGES.includes(statusForm.to_status) && !statusForm.next_followup_date) {
                ToastMsg('error', 'Please select the next followup date');
                return;
            }

            /* Closed Won without a value is the case this whole flow exists to
               stop. Reopen the modal rather than only complaining — the fix is
               two clicks away and the rep should not have to find it. */
            if (statusForm.to_status === 'Closed Won'
                && !dealValue?.deal_value
                && !frozenQuote?.accepted_plan) {
                setCloseWonOpen(true);
                ToastMsg('error', 'Closed Won needs a deal value');
                return;
            }

            /* Same idea for the handover: the CRE starts the implementation
               from it, and now is the last moment the rep still has the
               answers. Send them to the tab rather than only refuse — the form
               is one click away and hunting for it is not the rep's job. */
            if (statusForm.to_status === 'Closed Won' && handoverBlockers.length) {
                const names = handoverBlockers.slice(0, 3).map(f => f.label).join(', ');
                ToastMsg('error',
                    `Complete the CRE handover before marking this won — still missing: ${names}` +
                    (handoverBlockers.length > 3 ? ` and ${handoverBlockers.length - 3} more` : ''));
                setActiveTab('CRE Handover');
                return;
            }

            const stillBlank = stageFieldsMissing.filter(f => !String(leadFieldValues[f.key] ?? '').trim());
            if (stillBlank.length) {
                ToastMsg('error', `Fill in ${stillBlank.map(f => f.label).join(', ')} before moving this lead to ${statusForm.to_status}`);
                return;
            }

            if (statusForm.to_status === 'Demo Fixed' && !statusForm.demo_datetime) {
                ToastMsg('error', 'Please select the demo date & time');
                return;
            }

            if (STALL_REASON_STAGES.includes(statusForm.to_status) && !statusForm.stall_reason) {
                ToastMsg('error', 'Please select a stall reason');
                return;
            }

            const statusPayload = {
                customer_id: customerData?.id,
                from_status: statusForm.from_status,
                to_status: statusForm.to_status,
                lead_stage: statusForm.to_status == null ? customerData?.lead_stage : statusForm.to_status,
                remarks: statusForm.remarks,
                next_followup_date: statusForm.next_followup_date ? dayjs(statusForm.next_followup_date).format('YYYY-MM-DD HH:mm:ss') : null,
                demo_datetime: statusForm.demo_datetime ? dayjs(statusForm.demo_datetime).format('YYYY-MM-DD HH:mm:ss') : null,
                // Blank = the company's default presenter (filled in server-side).
                demo_by_id: DEMO_PRESENTER_STAGES.includes(statusForm.to_status) ? (statusForm.demo_by_id ?? null) : null,
                stall_reason: statusForm.stall_reason,
                channel: statusForm.channel,
                contact_outcome: statusForm.contact_outcome,
                // Only meaningful on Closed Won; the server ignores it otherwise,
                // and prefers a frozen quotation's figure over this one.
                deal_value: dealValue?.deal_value ?? null,
                quotation_id: dealValue?.quotation_id ?? null,
                // Values typed into the "needed for this stage" box. Only ones
                // the lead lacks, so a picker shown as a parent (country, to
                // list states) never overwrites what the lead already holds.
                lead_fields: Object.fromEntries(Object.entries(leadFieldValues).filter(([k, v]) =>
                    String(v ?? '').trim() && !String(customerData[k] ?? '').trim())),
            };

            const response = await create(CUSTOMERLOG, statusPayload);

            if (response.status === 'success') {
                ToastMsg("success", "Status updated successfully");
                setStatusModalOpen(false);
                setStatusForm({
                    to_status: null,
                    remarks: "",
                    next_followup_date: null,
                    demo_datetime: null,
                    stall_reason: null,
                    channel: null,
                    contact_outcome: null
                });
                setDealValue(null);
                setLeadFieldValues({});
                refetchCustomerDetails(customerId);
            } else {
                ToastMsg("error", response.message || "Failed to update status");
                // The rules changed since this page loaded; take the server's.
                if (response.stage_fields_required) loadStageFieldRules();
                /* The server refused on the handover — its list is the
                   authority, so take it over ours and open the tab holding
                   things up. Happens when somebody else edited the handover, or
                   an admin changed what is collected, since this page loaded. */
                if (response.handover_required) {
                    setHandover(h => (h ? { ...h, required_missing: response.missing_fields || [] } : h));
                    setActiveTab('CRE Handover');
                }
            }
        } catch (error) {
            console.error("Error updating status:", error);
            ToastMsg("error", "Failed to update status");
        }
    };

    const handleReassign = async () => {
        if (!reassignToId) { ToastMsg('error', 'Pick a rep first'); return; }
        try {
            setReassignSaving(true);
            const res = await update(ASSIGNEDTOUSER, {
                customer_id: [customerData.id],
                assigned_to_id: reassignToId,
            });
            if (res?.status === 'success') {
                ToastMsg('success', 'Lead reassigned');
                setReassignOpen(false);
                setReassignDialogOpen(false);
                setReassignToId(null);
                refetchCustomerDetails(customerId);
            } else {
                ToastMsg('error', res?.message || 'Failed to reassign');
            }
        } catch (e) {
            ToastMsg('error', 'Failed to reassign');
        } finally {
            setReassignSaving(false);
        }
    };

    // Add a note
    const handleAddNote = async () => {
        if (!noteText.trim()) { ToastMsg('error', 'Note cannot be empty'); return; }
        try {
            setSavingNote(true);
            const response = await create(CUSTOMERLOG, {
                customer_id: customerData.id,
                log_type: 'note',
                remarks: noteText.trim(),
                from_status: null,
                to_status: null,
                lead_stage: customerData.lead_stage,
            });
            if (response.status === 'success') {
                ToastMsg('success', 'Note added');
                setNoteText('');
                refetchCustomerDetails(customerId);
            } else {
                ToastMsg('error', response.message || 'Failed to add note');
            }
        } catch (e) {
            ToastMsg('error', 'Failed to add note');
        } finally {
            setSavingNote(false);
        }
    };

    // Edit an existing note
    const handleSaveEditNote = async (logId) => {
        if (!editingNoteText.trim()) { ToastMsg('error', 'Note cannot be empty'); return; }
        try {
            const response = await update(CUSTOMERLOG_EDIT(logId), { remarks: editingNoteText.trim() });
            if (response.status === 'success') {
                ToastMsg('success', 'Note updated');
                setEditingNoteId(null);
                setEditingNoteText('');
                refetchCustomerDetails(customerId);
            } else {
                ToastMsg('error', response.message || 'Failed to update note');
            }
        } catch (e) {
            ToastMsg('error', 'Failed to update note');
        }
    };

    // Save contact function
    const handleSaveContact = async () => {
        try {
            if (!newContact.name || !newContact.contact_number) {
                ToastMsg('error', 'Name and Contact Number are required');
                return;
            }


            // Check if there's already a primary contact
            const hasPrimaryContact = customerData.contacts.some(
                contact => contact.is_primary_contact === true
            );

            // Determine if this new contact should be primary based on type
            const isPrimaryContact = newContact.type === "Primary Contact";

            // If trying to add a new primary contact when one already exists
            if (isPrimaryContact && hasPrimaryContact) {
                ToastMsg('error', 'Primary contact already exists. Please change the existing primary contact first.');
                return;
            }

            // Create the contact object with is_primary_contact field
            const contactToSave = {
                ...newContact,
                is_primary_contact: isPrimaryContact
            };

            const contactPayload = {
                customer_id: customerData?.id,
                contacts: [contactToSave]
            };

            const response = await create(CUSTOMERDETAILSUPDATE, contactPayload);

            if (response.status === 'success') {
                ToastMsg("success", "Contact added successfully");
                setShowAddContact(false);
                setNewContact({
                    name: "",
                    designation: "",
                    contact_number: "",
                    email: "",
                    type: "Secondary Contact",
                    role: ""
                });
                refetchCustomerDetails(customerId);
            } else {
                ToastMsg("error", response.message || "Failed to add contact");
            }
        } catch (error) {
            console.error("Error adding contact:", error);
            ToastMsg("error", "Failed to add contact");
        }
    };

    // Save branch function
    // Save offer tool function
    // Update contact function
    const handleUpdateContact = async () => {
        try {
            if (!newContact.name || !newContact.contact_number) {
                ToastMsg('error', 'Name and Contact Number are required');
                return;
            }

            // Check if there's already a primary contact (excluding the one being edited)
            const hasOtherPrimaryContact = customerData.contacts.some(
                contact => contact.is_primary_contact === true && contact.id !== editingContact.id
            );

            // Determine if this updated contact should be primary based on type
            const isPrimaryContact = newContact.type === "Primary Contact";

            // If trying to update to primary contact when another primary already exists
            if (isPrimaryContact && hasOtherPrimaryContact) {
                ToastMsg('error', 'Primary contact already exists. Please change the existing primary contact first.');
                return;
            }

            const existingContactList = () => {
                let contacts = customerData.contacts;
                let modifiedList = contacts.map((item) => {
                    if (item.id === editingContact.id) {
                        return {
                            ...item,
                            ...newContact,
                            is_primary_contact: isPrimaryContact
                        };
                    }
                    return item;
                });
                return modifiedList;
            };

            const updatedContacts = existingContactList();

            const mapToParticularKey = {
                ...customerData,
                contacts: updatedContacts,
                customer_id: customerData.id,
                customer_name: customerData.customer_name
            };

            const response = await create(CUSTOMERDETAILSUPDATE, mapToParticularKey);

            if (response.status === 'success') {
                ToastMsg("success", "Contact updated successfully");
                setEditingContact(null);
                setNewContact({
                    name: "",
                    designation: "",
                    contact_number: "",
                    email: "",
                    type: "Secondary Contact",
                    role: ""
                });
                refetchCustomerDetails(customerId);
            } else {
                ToastMsg("error", response.message || "Failed to update contact");
            }
        } catch (error) {
            console.error("Error updating contact:", error);
            ToastMsg("error", "Failed to update contact");
        }
    };

    // Delete contact function
    const handleDeleteContact = async (contact = null) => {
        const contactToDelete = contact || editingContact;
        if (!contactToDelete) return;
        if (!window.confirm(`Remove ${contactToDelete.name || 'this contact'} from this customer?`)) return;

        try {
            const deletePayload = {
                id: contactToDelete.id,
                is_deleted: true,
                type: 'contact',
            };

            const response = await remove(CUSTOMERDETAILSUPDATE, deletePayload);

            if (response.status === 'success') {
                ToastMsg("success", "Contact deleted successfully");
                if (editingContact) {
                    setEditingContact(null);
                    setNewContact({
                        name: "",
                        designation: "",
                        contact_number: "",
                        email: "",
                        type: "Secondary Contact",
                        role: ""
                    });
                }
                refetchCustomerDetails(customerId);
            } else {
                ToastMsg("error", response.message || "Failed to delete contact");
            }
        } catch (error) {
            console.error("Error deleting contact:", error);
            ToastMsg("error", "Failed to delete contact");
        }
    };

    // Delete branch function
    // Delete offer tool function
    // Helper: POST to update endpoint and handle response
    const _postUpdate = async (payload, successMsg = "Saved successfully") => {
        const response = await create(CUSTOMERDETAILSUPDATE, payload);
        if (response.status === 'success') {
            ToastMsg("success", successMsg);
            refetchCustomerDetails(customerId);
            setIsEditMode(false);
            return true;
        } else {
            ToastMsg("error", response.message || "Failed to save");
            return false;
        }
    };

    // Save — Customer Information card (minimal payload)
    const handleSaveInfoCard = async () => {
        try {
            const saved = await _postUpdate({
                customer_id: customerData.id,
                customer_name: formData.customer_name,
                customer_type: formData.customer_type,
                is_active: formData.is_active,
                address: formData.address,
                country: formData.country,
                state: formData.state,
                city: formData.city,
                lead_source: formData.lead_source,
                // Sent even when empty: the API reads the pair together and
                // clears the partner for any source but the channel-partner one.
                sourced_by_partner: formData.sourced_by_partner || null,
                ...(projectAssignOn && formData.project_id && { project_id: formData.project_id }),
                ...(projectAssignOn && { extra_project_ids: formData.extra_project_ids || [] }),
                contract_details: {
                    ...(customerData.contract_details || {}),
                    potential: formData.potential,
                },
            }, "Customer information updated");
            // This page only loads leads in the active project, so a lead moved
            // elsewhere would vanish from its own page. Follow it instead — the
            // same switch-and-reload the ProjectSwitcher does.
            const target = projectOptions.find(p => p.id === formData.project_id);
            const activeProjectId = Number(sessionStorage.getItem('project_id')) || null;
            // Still shared into the project on screen? Then it stays visible here.
            const stillHere = (formData.extra_project_ids || []).includes(activeProjectId);
            if (saved && target && formData.project_id !== customerData.project_id
                    && activeProjectId && target.id !== activeProjectId && !stillHere) {
                ToastMsg("info", `Moved to ${target.name} — switching project`);
                switchProject(target);
                window.location.reload();
            }
        } catch (error) {
            console.error("Error saving info card:", error);
            ToastMsg("error", "Failed to save customer information");
        }
    };

    // Save — Estimated Deal Value card (minimal payload)
    const handleSaveDealCard = async () => {
        try {
            await _postUpdate({
                customer_id: customerData.id,
                financials: {
                    proposed_value: parseFloat(formData.proposed_value) || 0,
                    discount_offered: parseFloat(formData.discount_offered) || 0,
                    final_quoted_price: parseFloat(formData.final_quoted_price) || 0,
                    expected_closing_date: formData.expected_closing_date || null,
                },
            }, "Deal value updated");
        } catch (error) {
            console.error("Error saving deal card:", error);
            ToastMsg("error", "Failed to save deal value");
        }
    };

    // Save — full profile (used by hidden old right column)
    // Cancel edit function
    const handleCancelEdit = () => {
        if (customerData) {
            setFormData({
                customer_name: customerData.customer_name || "",
                customer_type: customerData.customer_type || "",
                is_active: customerData.is_active ?? true,
                lead_source: customerData.lead_source || "",
                sourced_by_partner: customerData.sourced_by_partner || null,
                project_id: customerData.project_id || null,
                extra_project_ids: (customerData.extra_projects || []).map(p => p.id),
                address: customerData.address || "",
                country: customerData.country || DEFAULT_COUNTRY,
                state: stateName(customerData.country || DEFAULT_COUNTRY, customerData.state),
                city: customerData.city || "",
                contract_period: customerData.contract_details?.contract_period || "",
                type_of_hospital: customerData.contract_details?.type_of_hospital || "",
                number_of_beds: customerData.contract_details?.number_of_beds || "",
                contract_volume: customerData.contract_details?.contract_volume || "",
                potential: customerData.contract_details?.potential || "",
                customer_competitor: customerData.contract_details?.customer_competitor || "",
                current_software_used: customerData.technical_info?.current_software_used || "",
                server_type: customerData.technical_info?.server_type || "",
                no_of_systems: customerData.technical_info?.no_of_systems || "",
                no_of_users: customerData.technical_info?.no_of_users || "",
                internet_connectivity: customerData.technical_info?.internet_connectivity || "",
                beds: customerData.hospital_capacity?.beds || "",
                opd_per_day: customerData.hospital_capacity?.opd_per_day || "",
                ip_admissions_per_day: customerData.hospital_capacity?.ip_admissions_per_day || "",
                doctors: customerData.hospital_capacity?.doctors || "",
                departments: customerData.hospital_capacity?.departments || "",
                proposed_value: customerData.financials?.proposed_value || "",
                discount_offered: customerData.financials?.discount_offered || "",
                final_quoted_price: customerData.financials?.final_quoted_price || "",
                expected_closing_date: customerData.financials?.expected_closing_date || "",
                last_discussion_summary: customerData.followup?.last_discussion_summary || "",
                next_followup_date: customerData.followup?.next_followup_date || "",
                recent_conversation: customerData.followup?.recent_conversation || "",
                pending_commitments: customerData.followup?.pending_commitments || "",
                remarks: customerData.followup?.remarks || "",
                potential_threat: customerData.competition_analysis?.potential_threat || "",
                present_objective: customerData.competition_analysis?.present_objective || "",
                long_term_objective: customerData.competition_analysis?.long_term_objective || "",
                value_addition: customerData.competition_analysis?.value_addition || "",
            });
        }
        setIsEditMode(false);
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => {
            const newFormData = { ...prev, [field]: value };

            // Auto-calculate final quoted price
            if (isEditMode && (field === 'proposed_value' || field === 'discount_offered')) {
                const proposed = parseFloat(newFormData.proposed_value) || 0;
                const discount = parseFloat(newFormData.discount_offered) || 0;

                if (proposed > 0) {
                    const discountAmount = (proposed * discount) / 100;
                    const finalPrice = proposed - discountAmount;
                    newFormData.final_quoted_price = finalPrice.toFixed(2);
                } else {
                    newFormData.final_quoted_price = "";
                }
            }

            return newFormData;
        });
    };

    const handleAddContact = () => {
        setShowAddContact(true);
        setEditingContact(null);
        setNewContact({
            name: "",
            designation: "",
            contact_number: "",
            email: "",
            type: "Secondary Contact",
            role: ""
        });
    };

    const handleEditContact = (contact) => {
        setEditingContact(contact);
        setShowAddContact(false);
        setNewContact({
            name: contact.name || "",
            designation: contact.designation || "",
            contact_number: contact.contact_number || "",
            email: contact.email || "",
            type: !contact.type && contact.is_primary_contact ? "Primary Contact" : contact.type || "Secondary Contact",
            is_primary_contact: contact.is_primary_contact ? "Primary Contact" : "",
            role: contact.role || ""
        });
    };

    const handleCancelAddContact = () => {
        setShowAddContact(false);
        setEditingContact(null);
        setNewContact({
            name: "",
            designation: "",
            contact_number: "",
            email: "",
            type: "Secondary Contact",
            role: ""
        });
    };

    const handleNewContactChange = (field, value) => {
        setNewContact(prev => ({ ...prev, [field]: value }));
    };

    const handleStatusFormChange = (field, value) => {
        setStatusForm(prev => {
            if (field === 'to_status') {
                // When to_status changes, set from_status to the current lead_stage
                // and clear stage-specific fields so stale values are never submitted
                return {
                    ...prev,
                    [field]: value,
                    from_status: customerData.lead_stage || 'New Lead', // Set from_status to current status
                    demo_datetime: value === 'Demo Fixed' ? prev.demo_datetime : null,
                    stall_reason: STALL_REASON_STAGES.includes(value) ? prev.stall_reason : null
                };
            }
            return { ...prev, [field]: value };
        });

        /* Ask for the value the moment Closed Won is picked, not on submit. A rep
           who fills the whole form and is then told to go and freeze a quotation
           has to redo it. Moving off Closed Won drops whatever was captured, so a
           value can never be attached to a different outcome. */
        if (field === 'to_status') {
            if (value === 'Closed Won') {
                setCloseWonOpen(true);
            } else {
                setDealValue(null);
            }
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Not scheduled';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Delegates to the shared formatter so this page and the customers list
    // render the same field the same way. Keeps the '' fallback the call sites
    // below were written against, rather than the shared default of '—'.
    const formatDateTime = (dateString) => fmtDateTime(dateString, '');

    /* Timeline colours and icons per stage. Keyed on the real stage names: the
       old maps used 'Proposal Sent' / 'Won' / 'Lost', which no lead has, so
       most events fell back to grey. Full class strings so Tailwind keeps them. */
    const STAGE_TONE = {
        'New Lead':      { dot: 'bg-blue-500',    card: 'bg-blue-50 border-blue-200 dark:bg-blue-900/10 dark:border-blue-800',          icon: Sparkles },
        'Unanswered':    { dot: 'bg-slate-500',   card: 'bg-slate-50 border-slate-200 dark:bg-slate-800/40 dark:border-slate-700',       icon: Phone },
        'Contacted':     { dot: 'bg-cyan-500',    card: 'bg-cyan-50 border-cyan-200 dark:bg-cyan-900/10 dark:border-cyan-800',          icon: Phone },
        'Qualified':     { dot: 'bg-purple-500',  card: 'bg-purple-50 border-purple-200 dark:bg-purple-900/10 dark:border-purple-800',  icon: CheckCircle },
        'Demo Fixed':    { dot: 'bg-indigo-500',  card: 'bg-indigo-50 border-indigo-200 dark:bg-indigo-900/10 dark:border-indigo-800',  icon: Calendar },
        'Demo Done':     { dot: 'bg-violet-500',  card: 'bg-violet-50 border-violet-200 dark:bg-violet-900/10 dark:border-violet-800',  icon: CheckSquare },
        'No Show':       { dot: 'bg-rose-500',    card: 'bg-rose-50 border-rose-200 dark:bg-rose-900/10 dark:border-rose-800',          icon: XCircle },
        'Proposal':      { dot: 'bg-orange-500',  card: 'bg-orange-50 border-orange-200 dark:bg-orange-900/10 dark:border-orange-800',  icon: Send },
        'Negotiation':   { dot: 'bg-yellow-500',  card: 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/10 dark:border-yellow-800',  icon: Handshake },
        'On Hold':       { dot: 'bg-gray-500',    card: 'bg-gray-50 border-gray-200 dark:bg-gray-700/50 dark:border-gray-600',          icon: Pause },
        'Closed Won':    { dot: 'bg-green-500',   card: 'bg-green-50 border-green-200 dark:bg-green-900/10 dark:border-green-800',      icon: Trophy },
        'Onboarded':     { dot: 'bg-emerald-500', card: 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/10 dark:border-emerald-800', icon: BadgeCheck },
        'Closed Lost':   { dot: 'bg-red-500',     card: 'bg-red-50 border-red-200 dark:bg-red-900/10 dark:border-red-800',              icon: XCircle },
        'Dis-Qualified': { dot: 'bg-red-400',     card: 'bg-red-50 border-red-200 dark:bg-red-900/10 dark:border-red-800',              icon: XCircle },
        'Nurture Lead':       { dot: 'bg-teal-500',    card: 'bg-teal-50 border-teal-200 dark:bg-teal-900/10 dark:border-teal-800',          icon: Clock },
    };
    const toneOf = (status) => STAGE_TONE[status] || null;
    const getTimelineDotColor = (status) => toneOf(status)?.dot || 'bg-gray-400';
    const getTimelineCardColor = (status) =>
        toneOf(status)?.card || 'bg-gray-50 border-gray-200 dark:bg-gray-700/50 dark:border-gray-600';
    const getStatusIcon = (status) => {
        const Icon = toneOf(status)?.icon || Clock;
        return <Icon className="h-3.5 w-3.5 text-white" />;
    };
    const getStatusDotColor = getTimelineDotColor;

    const getStatusTextColor = (status) => {
        switch (status) {
            case 'Closed Won':
                return "text-green-700 dark:text-green-300";
            case 'Closed Lost':
                return "text-red-700 dark:text-red-300";
            case 'On Hold':
                return "text-orange-700 dark:text-orange-300";
            case 'New Lead':
                return "text-gray-700 dark:text-gray-300";
            case 'Contacted':
                return "text-blue-700 dark:text-blue-300";
            case 'Qualified':
                return "text-purple-700 dark:text-purple-300";
            case 'Proposal':
                return "text-indigo-700 dark:text-indigo-300";
            case 'Negotiation':
                return "text-yellow-700 dark:text-yellow-300";
            case 'Demo Fixed':
                return "text-cyan-700 dark:text-cyan-300";
            case 'Demo Done':
                return "text-teal-700 dark:text-teal-300";
            case 'No Show':
                return "text-red-700 dark:text-red-300";
            default:
                return "text-gray-700 dark:text-gray-300";
        }
    };

    // First load of a customer only: the same shape as the page, so nothing
    // jumps when the data arrives.
    if (loading) {
        return (
            <ContainerCard>
                <div className="space-y-3" aria-busy="true" aria-label="Loading customer">
                    <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
                        <div className="flex items-center gap-3">
                            <Skeleton className="h-10 w-10 rounded-xl" />
                            <div className="flex-1 space-y-2">
                                <Skeleton className="h-5 w-56" />
                                <Skeleton className="h-3 w-80 max-w-full" />
                            </div>
                            <Skeleton className="hidden sm:block h-8 w-28" />
                        </div>
                        <div className="hidden md:flex gap-2">
                            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-8 w-24" />)}
                        </div>
                    </div>
                    <div className="flex gap-2 overflow-hidden">
                        {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-6 w-24 rounded-full flex-shrink-0" />)}
                    </div>
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_320px] xl:grid-cols-[minmax(0,1fr)_350px]">
                        <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-4">
                            <div className="flex gap-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-5 w-20" />)}</div>
                            {[...Array(5)].map((_, i) => (
                                <div key={i} className="flex gap-3">
                                    <Skeleton className="h-8 w-8 rounded-full" />
                                    <div className="flex-1 space-y-2"><Skeleton className="h-4 w-1/3" /><Skeleton className="h-12 w-full" /></div>
                                </div>
                            ))}
                        </div>
                        <div className="space-y-3">
                            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 w-full rounded-xl" />)}
                        </div>
                    </div>
                </div>
            </ContainerCard>
        );
    }

    // ── What the header, the stepper and the context panel show. Derived from
    //    data the page already has; nothing here fetches.
    const ownerName = customerData.current_assignee || customerData.assigned_to_name || '';
    const canWhatsApp = hasFeature('whatsapp_campaigns') || hasFeature('whatsapp_inbox');
    const canEmail = hasFeature('email_integration');

    // Every number on file, the primary contact's first, each once.
    const phones = (() => {
        const seen = new Set();
        const out = [];
        const add = (number, label) => {
            const key = String(number || '').replace(/\D/g, '').slice(-10);
            if (!key || seen.has(key)) return;
            seen.add(key);
            out.push({ number, label });
        };
        const contacts = [...(customerData.contacts || [])]
            .sort((a, b) => (b.is_primary_contact ? 1 : 0) - (a.is_primary_contact ? 1 : 0));
        contacts.forEach(c => add(c.contact_number,
            [c.name, c.designation].filter(Boolean).join(' · ') || 'Contact'));
        add(customerData.contact_number, customerData.contact_name || 'Customer');
        return out;
    })();

    // The figure a rep quotes from: the accepted quotation, else the deal value.
    const headerValue = isChannelPartner ? null
        : frozenQuote?.accepted_plan?.grand_total ? { amount: frozenQuote.accepted_plan.grand_total, label: 'Accepted quotation' }
        : Number(customerData.financials?.final_quoted_price) > 0 ? { amount: customerData.financials.final_quoted_price, label: 'Final price' }
        : Number(customerData.financials?.proposed_value) > 0 ? { amount: customerData.financials.proposed_value, label: 'Proposed value' }
        : null;

    // The tabs this customer has, in the order the page has always offered them.
    const availableTabs = ['All Activities', 'CRM Events', 'Notes', 'Tasks', 'Calls',
        ...(canEmail ? ['Emails'] : []),
        ...(canWhatsApp ? ['WhatsApp'] : []),
        // A partner's book of leads; only a partner has one.
        ...(isChannelPartner ? ['Sourced Leads'] : []),
        // Only where eMedHub has actually awarded something.
        ...(hasReferralBenefits ? ['Referral Benefits'] : []),
        // Every enquiry from this person and the other leads on their number.
        ...(!isChannelPartner ? ['Enquiries'] : []),
        'Documents', 'Engagement', 'Pain Points', 'Hospital Profile',
        // What the organisation's own website says, with sources (research/).
        ...(!isChannelPartner && canResearch ? ['Company Research'] : []),
        'Custom Fields',
        // Only where the tenant collects a handover, or this lead already has one.
        ...((handover?.is_enabled || handover?.has_handover) ? ['CRE Handover'] : [])];

    const showWorkspace = () => workspaceRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const goToTab = (tab) => { setActiveTab(tab); setTimeout(showWorkspace, 0); };
    const openWhatsApp = () => goToTab('WhatsApp');
    // A lead has no email of its own — addresses live on its contacts. Primary
    // contact first, then any contact that has one; same pick as the side panel.
    const leadEmail = () => {
        const contacts = customerData.contacts || [];
        return (contacts.find(c => c.is_primary_contact && c.email) || contacts.find(c => c.email))?.email
            || customerData.email || '';
    };
    // `prefill` is a Reply / Forward draft from the reading panel (EmailReader).
    const startCompose = (prefill = {}) => {
        setEmailForm({
            from_account_id: emailAccounts[0]?.id || null,
            to: leadEmail(),
            cc: '', bcc: '', subject: '', body_html: '',
            ...prefill,
        });
        setShowCcBcc(!!prefill.cc);
        setComposeOpen(true);
    };
    const emailQ = emailQuery.trim().toLowerCase();
    const shownEmails = emails.filter(em =>
        (emailDir === 'all' || em.direction === emailDir)
        && (!emailQ || [em.subject, em.from_email, em.to_email, em.sent_by_name]
            .some(v => (v || '').toLowerCase().includes(emailQ))));
    // Conversations, for stepping through them in the reading panel.
    const emailThreads = groupThreads(shownEmails);
    const emailCounts = {
        all: emails.length,
        sent: emails.filter(em => em.direction === 'sent').length,
        received: emails.filter(em => em.direction === 'received').length,
    };
    // Compose needs the accounts and templates the Emails tab loads on entry.
    const openEmail = () => { goToTab('Emails'); startCompose(); };
    // Manual only: pulls this lead's mail from every connected cPanel /
    // GoDaddy / IMAP mailbox, then reloads the list.
    const canSyncEmail = emailAccounts.some(a => ['cpanel', 'godaddy', 'imap'].includes(a.provider));
    const syncLeadEmail = async () => {
        setSyncingEmail(true);
        const res = await create(EMAIL_CUSTOMER_SYNC_URL(customerId), {});
        setSyncingEmail(false);
        if (res?.status !== 'success') {
            ToastMsg('error', res?.message || 'Sync failed');
            return;
        }
        const d = res.data || {};
        if (d.message) ToastMsg('info', d.message);
        else ToastMsg(d.stored ? 'success' : 'info', d.stored ? `${d.stored} new email${d.stored === 1 ? '' : 's'} synced` : 'No new emails');
        (d.errors || []).forEach(e => ToastMsg('warning', e));
        const r = await fetch(`${EMAIL_MESSAGES_URL}?customer_id=${customerId}`);
        if (r?.status === 'success') setEmails(r.data || []);
    };
    const openNote = () => {
        goToTab('Notes');
        setTimeout(() => document.getElementById('customer-note-input')?.focus(), 50);
    };
    const openStatusModal = (stage) => {
        if (stage && stage !== customerData.lead_stage) handleStatusFormChange('to_status', stage);
        setStatusModalOpen(true);
    };
    /* Back from Nurture into the pipeline: the normal status form, preset to
       Contacted, so the move is logged like any other (nurture.py ends the stay). */
    const reviveFromNurture = () => {
        openStatusModal('Contacted');
        handleStatusFormChange('remarks', 'Revived from Nurture Lead: ');
    };
    const onCalledFromHeader = () => { onStaffCalled(); setActiveTab('Calls'); };
    const editDetails = () => {
        setEditingCard('info');
        setIsEditMode(true);
        setTimeout(() => detailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
    };

    const moreItems = [
        { key: 'edit', label: 'Edit details', onClick: editDetails },
        { key: 'reassign', label: 'Reassign…', onClick: () => setReassignDialogOpen(true) },
        { key: 'owner', label: 'Owner history', onClick: () => setOwnerHistoryOpen(true) },
        ...(!isChannelPartner && canResearch ? [{ key: 'research', label: 'Research company', onClick: () => goToTab('Company Research') }] : []),
        ...((customerData.lead_stage === 'Closed Won' || frozenQuote?.accepted_plan)
            ? [{ key: 'onboard', label: onboardLabel, onClick: onboardAction }] : []),
        ...(frozenQuote?.id ? [{ key: 'quote', label: 'View quotation', onClick: () => navigate(`/quotations/${frozenQuote.id}`) }] : []),
    ];

    const nextFollowUpCard = !isChannelPartner && (
        <NextFollowUpCard date={customerData.followup?.next_followup_date}
            needsOne={!NO_FOLLOWUP_STAGES.includes(customerData.lead_stage)}
            onUpdate={() => openStatusModal()} />
    );

    return (
        <ContainerCard>
            <DialogModal open={ownerHistoryOpen} onClose={() => setOwnerHistoryOpen(false)} showActions={false}
                width="820px" title={`Owner history${customerData?.customer_name ? ` — ${customerData.customer_name}` : ''}`}>
                {ownerHistoryOpen && customerData?.id && <OwnerHistory leadId={customerData.id} />}
            </DialogModal>

            {/* Reassignment banner — the lead hit the Unanswered limit set in Settings → Lead Assignment */}
            {customerData?.needs_reassignment && (
                <div className="mb-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700/50 rounded-lg">
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                                This lead has been marked Unanswered {customerData.unanswered_count} times in a row
                                {customerData.current_assignee ? ` by ${customerData.current_assignee}` : ''}.
                            </p>
                            <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                                Consider reassigning to another rep — a fresh voice often gets a response.
                            </p>
                            {reassignOpen ? (
                                <div className="flex flex-wrap items-center gap-2 mt-2">
                                    <Select
                                        size="small"
                                        placeholder="Pick a rep"
                                        value={reassignToId || undefined}
                                        onChange={v => setReassignToId(v)}
                                        showSearch
                                        optionFilterProp="children"
                                        style={{ minWidth: 220 }}
                                        options={taskUsers
                                            .filter(u => u.id !== customerData.assigned_to_id)
                                            .map(u => ({
                                                value: u.id,
                                                label: u.login_fname
                                                    ? `${u.login_fname}${u.login_lname ? ' ' + u.login_lname : ''}`
                                                    : (u.login_name || `User ${u.id}`),
                                            }))}
                                    />
                                    <Button
                                        size="sm"
                                        onClick={handleReassign}
                                        disabled={reassignSaving || !reassignToId}
                                        className="h-7 px-3 text-xs bg-amber-600 hover:bg-amber-700 text-white"
                                    >
                                        {reassignSaving ? 'Saving…' : 'Confirm Reassign'}
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => { setReassignOpen(false); setReassignToId(null); }}
                                        className="h-7 px-3 text-xs"
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            ) : (
                                <button
                                    onClick={() => setReassignOpen(true)}
                                    className="mt-2 h-7 px-3 text-xs font-medium bg-amber-600 hover:bg-amber-700 text-white rounded-md inline-flex items-center gap-1"
                                >
                                    <Users className="h-3 w-3" />
                                    Reassign
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Header: who, stage, owner, value, actions ── */}
            <div className="space-y-3 pb-16 md:pb-0">
            <CustomerHeader customer={customerData} ownerName={ownerName}
                valueAmount={headerValue?.amount} valueLabel={headerValue?.label}
                refreshing={refreshing} onBack={handleBack} onWhy={() => setOwnerHistoryOpen(true)}
                statusRow={customerId && (
                    <LeadStatusChips customerId={customerId} contacts={customerData.contacts}
                        leadPhone={customerData.contact_number} flags={contactVerification.flags}
                        showResearch={canResearch} onOpenResearch={() => goToTab('Company Research')} />
                )}
                actions={
                    <QuickActions phones={phones} customerId={customerId} onCalled={onCalledFromHeader}
                        onWhatsApp={canWhatsApp ? openWhatsApp : null} onEmail={canEmail ? openEmail : null}
                        onNote={openNote} onStage={() => openStatusModal()} moreItems={moreItems} />
                } />

            <StageProgress stage={customerData.lead_stage} history={customerData.status_log}
                isChannelPartner={isChannelPartner} onPick={stage => openStatusModal(stage)} />

            {/* Phones and tablets: the next follow-up before the activity. */}
            <div className="lg:hidden">{nextFollowUpCard}</div>

            {/* ── Workspace (left) + context (right) ── */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_320px] xl:grid-cols-[minmax(0,1fr)_350px] items-start">
                {/* ── RIGHT PANEL - Activity Timeline ── */}
                {/* Right Panel - Activity Timeline */}
                <div ref={workspaceRef} className="min-w-0 space-y-3 scroll-mt-4">
                    {customerId && !isChannelPartner && (
                        <LeadReturnBanner customerId={customerId} refreshKey={customerData.lead_stage}
                            onOpenEnquiries={() => { setActiveTab('Enquiries'); }} />
                    )}
                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                        <TabNav available={availableTabs} activeTab={activeTab} onChange={setActiveTab} />
                        <div className="p-3 sm:p-4">

                        {/* CRM Events: stage changes. The form itself is the modal
                            opened by "Update stage / follow-up". */}
                        {activeTab === 'CRM Events' && (
                            <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-dashed border-gray-300 p-3 dark:border-gray-600">
                                <p className="text-xs text-gray-600 dark:text-gray-300">
                                    Log a call outcome, move the stage or book the next follow-up.
                                </p>
                                <Button size="sm" onClick={() => openStatusModal()} className="h-8 text-xs bg-primary text-white">
                                    <TrendingUp className="h-3.5 w-3.5 mr-1.5" /> Update stage / follow-up
                                </Button>
                            </div>
                        )}

                        {/* Add note form — Notes tab only */}
                        {activeTab === 'Notes' && (
                            <div className="mb-4 p-4 bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 rounded-xl border-2 border-yellow-200 dark:border-yellow-800">
                                <div className="flex items-center gap-2 mb-3">
                                    <div className="w-7 h-7 bg-yellow-500 rounded-lg flex items-center justify-center flex-shrink-0">
                                        <MessageSquare className="h-3.5 w-3.5 text-white" />
                                    </div>
                                    <span className="text-sm font-bold text-gray-900 dark:text-white">Add Note</span>
                                </div>
                                <Textarea
                                    id="customer-note-input"
                                    value={noteText}
                                    onChange={(e) => setNoteText(e.target.value)}
                                    placeholder="Write a note about this customer..."
                                    rows={4}
                                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 resize-none text-sm mb-3"
                                />
                                <Button
                                    onClick={handleAddNote}
                                    disabled={savingNote || !noteText.trim()}
                                    size="sm"
                                    className="bg-yellow-500 hover:bg-yellow-600 text-white h-8 text-xs px-4">
                                    {savingNote ? 'Saving...' : 'Save Note'}
                                </Button>
                            </div>
                        )}

                        {/* ── Calls tab — provider call log (with recordings) above the
                            call notes from the timeline below. ── */}
                        {activeTab === 'Calls' && customerId && (
                            <CallHistory customerId={customerId} refreshKey={callRefreshKey} className="mb-4" />
                        )}

                        {/* ── Engagement tab ── */}
                        {activeTab === 'Engagement' && customerId && (
                            <EngagementDashboard customerId={customerId} />
                        )}

                        {/* ── Pain Points tab ── */}
                        {activeTab === 'Pain Points' && customerId && (
                            <PainPointsPanel customerId={customerId} />
                        )}

                        {/* ── Hospital Profile tab ── */}
                        {activeTab === 'Sourced Leads' && customerId && (
                            <PartnerSourcedLeadsPanel partnerId={customerId} />
                        )}

                        {activeTab === 'Referral Benefits' && customerId && (
                            <ReferralBenefitsPanel customerId={customerId} />
                        )}

                        {activeTab === 'Enquiries' && customerId && (
                            <LeadEnquiriesPanel customerId={customerId} refreshKey={customerData.lead_stage} />
                        )}

                        {activeTab === 'Hospital Profile' && customerId && (
                            <HospitalProfilePanel customerId={customerId} />
                        )}

                        {activeTab === 'Company Research' && customerId && (
                            <CompanyResearchPanel customerId={customerId} />
                        )}

                        {/* ── CRE Handover tab — what sales hands the implementation
                            team once the deal is won. Switched on per tenant under
                            Settings → CRE Handover. ── */}
                        {activeTab === 'CRE Handover' && customerId && (
                            <CREHandoverPanel
                                customerId={customerId}
                                customerName={customerData.customer_name}
                                initial={handover}
                                // The panel holds its own copy while editing;
                                // this puts the saved result back here so the
                                // Closed Won check is not testing a stale list.
                                onSaved={res => setHandover(h => ({ ...h, ...res }))}
                            />
                        )}

                        {/* ── Custom Fields tab — the admin-defined attributes.
                            Was in the left rail, where 33 fields in a 280px
                            column pushed the deal card and AI panel off screen. */}
                        {activeTab === 'Custom Fields' && customerId && (
                            <CustomerAttributesPanel
                                customerId={customerId}
                                customerName={customerData.customer_name}
                            />
                        )}

                        {/* ── Tasks tab ── */}
                        {activeTab === 'Tasks' && (
                            <div className="space-y-4">
                                {/* Create task form */}
                                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                                    <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-3">New Task</p>
                                    <div className="space-y-3">
                                        <input
                                            type="text"
                                            value={taskForm.title}
                                            onChange={e => setTaskForm(p => ({ ...p, title: e.target.value }))}
                                            placeholder="Task title *"
                                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                                        />
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                            <Select
                                                value={taskForm.assigned_to_id || undefined}
                                                onChange={val => setTaskForm(p => ({ ...p, assigned_to_id: val }))}
                                                placeholder="Assign to *"
                                                className="w-full"
                                                options={taskUsers.map(u => ({ value: u.id, label: `${u.login_fname} ${u.login_lname || ''}`.trim() }))}
                                            />
                                            <DatePicker
                                                value={taskForm.due_date ? dayjs(taskForm.due_date) : null}
                                                onChange={val => setTaskForm(p => ({ ...p, due_date: val }))}
                                                placeholder="Due date"
                                                format="DD/MM/YYYY"
                                                className="w-full"
                                            />
                                            <Select
                                                value={taskForm.priority}
                                                onChange={val => setTaskForm(p => ({ ...p, priority: val }))}
                                                className="w-full"
                                                options={[
                                                    { value: 'Low', label: 'Low' },
                                                    { value: 'Medium', label: 'Medium' },
                                                    { value: 'High', label: 'High' },
                                                ]}
                                            />
                                        </div>
                                        <div className="flex justify-end">
                                            <button
                                                disabled={savingTask || !taskForm.title.trim() || !taskForm.assigned_to_id}
                                                onClick={async () => {
                                                    setSavingTask(true);
                                                    const payload = {
                                                        title: taskForm.title,
                                                        assigned_to_id: taskForm.assigned_to_id,
                                                        due_date: taskForm.due_date ? dayjs(taskForm.due_date).format('YYYY-MM-DD') : null,
                                                        priority: taskForm.priority,
                                                        customer_id: customerId,
                                                        customer_name: customerData.customer_name || '',
                                                    };
                                                    const res = await create(TASKS_URL, payload);
                                                    if (res?.status === 'success') {
                                                        ToastMsg('success', 'Task created');
                                                        setTaskForm({ title: '', assigned_to_id: null, due_date: null, priority: 'Medium' });
                                                        fetch(TASKS_URL + `?customer_id=${customerId}`).then(r => {
                                                            if (r?.status === 'success') setCustomerTasks(r.data || []);
                                                        });
                                                    } else {
                                                        ToastMsg('error', res?.message || 'Failed to create task');
                                                    }
                                                    setSavingTask(false);
                                                }}
                                                className="px-4 py-1.5 text-xs font-medium bg-primary hover:bg-primary/90 disabled:opacity-50 text-white rounded-lg transition-colors">
                                                {savingTask ? 'Saving...' : 'Add Task'}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Task list */}
                                {customerTasks.length === 0 ? (
                                    <div className="text-center py-6">
                                        <p className="text-sm text-gray-400 dark:text-gray-500">No tasks yet</p>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {customerTasks.map(task => (
                                            <div key={task.id} className="flex items-center gap-3 p-3 bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg">
                                                <div className={cn("w-2 h-2 rounded-full flex-shrink-0",
                                                    task.priority === 'High' ? 'bg-red-500' :
                                                    task.priority === 'Medium' ? 'bg-yellow-500' : 'bg-green-500'
                                                )} />
                                                <div className="flex-1 min-w-0">
                                                    <p className={cn("text-sm font-medium text-gray-900 dark:text-white truncate",
                                                        task.task_status === 'Completed' && "line-through text-gray-400"
                                                    )}>{task.title}</p>
                                                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                                        {task.assigned_to_name && (
                                                            <span className="text-xs text-gray-500 dark:text-gray-400">→ {task.assigned_to_name}</span>
                                                        )}
                                                        {task.due_date && (
                                                            <span className="text-xs text-gray-400">{dayjs(task.due_date).format('DD MMM')}</span>
                                                        )}
                                                    </div>
                                                </div>
                                                <span className={cn("px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0",
                                                    task.task_status === 'Completed' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                                    task.task_status === 'In Progress' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                                                    'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                                                )}>{task.task_status}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Emails tab — overview, toolbar and conversation list */}
                        {activeTab === 'Emails' && (
                            <EmailsTab
                                emails={emails}
                                shownEmails={shownEmails}
                                emailCounts={emailCounts}
                                emailDir={emailDir}
                                setEmailDir={setEmailDir}
                                emailQuery={emailQuery}
                                setEmailQuery={setEmailQuery}
                                openEmailId={openEmailId}
                                onOpen={setOpenEmailId}
                                onCompose={() => startCompose()}
                                onSync={syncLeadEmail}
                                syncing={syncingEmail}
                                canSync={canSyncEmail}
                                lastSyncedAt={emailAccounts.map(a => a.last_synced_at).filter(Boolean).sort().pop()}
                                contacts={customerData.contacts || []}
                                accounts={emailAccounts}
                            />
                        )}

                        {/* Reading panel — Reply / Forward hand a draft to Compose */}
                        <EmailReader
                            emailId={openEmailId}
                            ids={emailThreads.map(t => t.latest.id)}
                            threadIds={(emailThreads.find(t => t.messages.some(m => m.id === openEmailId))?.messages || []).map(m => m.id)}
                            contacts={customerData.contacts || []}
                            accounts={emailAccounts}
                            onNavigate={setOpenEmailId}
                            onClose={() => setOpenEmailId(null)}
                            onReply={draft => { setOpenEmailId(null); startCompose(draft); }}
                            canSend={canEmail}
                        />

                        {/* Compose Email Modal */}
                        <DialogModal
                            open={composeOpen}
                            onClose={() => setComposeOpen(false)}
                            title="Compose Email"
                            width="700px"
                            showActions={false}
                        >
                            <div className="space-y-3">
                                {/* From */}
                                <div>
                                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">From</label>
                                    {emailAccounts.length === 0 ? (
                                        <p className="text-xs text-orange-600 dark:text-orange-400 p-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                                            No email account connected.{' '}
                                            <a href="/settings/email-integration" className="underline font-medium">Connect one in Settings</a>.
                                        </p>
                                    ) : (
                                        <Select
                                            className="w-full"
                                            value={emailForm.from_account_id}
                                            onChange={v => setEmailForm(p => ({ ...p, from_account_id: v }))}
                                            options={emailAccounts.map(a => ({ value: a.id, label: `${a.email_address} (${a.provider_label || a.provider})` }))}
                                        />
                                    )}
                                </div>
                                {/* To */}
                                <div>
                                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">To</label>
                                    <Input value={emailForm.to} onChange={e => setEmailForm(p => ({ ...p, to: e.target.value }))} placeholder="recipient@example.com" />
                                </div>
                                {/* CC / BCC */}
                                {!showCcBcc ? (
                                    <button onClick={() => setShowCcBcc(true)} className="text-xs text-blue-500 hover:text-blue-700">+ Add CC / BCC</button>
                                ) : (
                                    <>
                                        <div>
                                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">CC</label>
                                            <Input value={emailForm.cc} onChange={e => setEmailForm(p => ({ ...p, cc: e.target.value }))} placeholder="cc@example.com" />
                                        </div>
                                        <div>
                                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">BCC</label>
                                            <Input value={emailForm.bcc} onChange={e => setEmailForm(p => ({ ...p, bcc: e.target.value }))} placeholder="bcc@example.com" />
                                        </div>
                                    </>
                                )}
                                {/* Subject */}
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Subject</label>
                                        <button
                                            onClick={() => { setTemplateSearch(''); setTemplatePickerOpen(true); }}
                                            className="text-xs text-blue-500 hover:text-blue-700 font-medium"
                                        >
                                            Use Template
                                        </button>
                                    </div>
                                    <Input value={emailForm.subject} onChange={e => setEmailForm(p => ({ ...p, subject: e.target.value }))} placeholder="Email subject" />
                                </div>
                                {/* Body */}
                                <div>
                                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">Body</label>
                                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                                        <ReactQuill
                                            theme="snow"
                                            value={emailForm.body_html}
                                            onChange={v => setEmailForm(p => ({ ...p, body_html: v }))}
                                            style={{ height: '250px' }}
                                        />
                                    </div>
                                    <div style={{ height: '42px' }} />
                                </div>
                                {/* Actions */}
                                <div className="flex justify-end gap-3 pt-1">
                                    <Button variant="outline" onClick={() => setComposeOpen(false)} className="text-sm">Cancel</Button>
                                    <Button
                                        disabled={sendingEmail || !emailForm.to || !emailForm.subject}
                                        onClick={async () => {
                                            setSendingEmail(true);
                                            const payload = {
                                                ...emailForm,
                                                customer_id: customerId,
                                                customer_name: customerData.customer_name || '',
                                                sales_rep_name: sessionStorage.getItem('login_name') || '',
                                            };
                                            const res = await create(EMAIL_SEND_URL, payload);
                                            setSendingEmail(false);
                                            if (res?.status === 'success') {
                                                ToastMsg('success', 'Email sent');
                                                setComposeOpen(false);
                                                fetch(`${EMAIL_MESSAGES_URL}?customer_id=${customerId}`).then(r => {
                                                    if (r?.status === 'success') setEmails(r.data || []);
                                                });
                                            } else {
                                                ToastMsg('error', res?.message || 'Failed to send email');
                                            }
                                        }}
                                        className="text-sm flex items-center gap-2"
                                    >
                                        {sendingEmail ? <span className="animate-spin text-base">⟳</span> : <Send className="h-4 w-4" />}
                                        Send
                                    </Button>
                                </div>
                            </div>
                        </DialogModal>

                        {/* Template Picker Modal */}
                        <DialogModal
                            open={templatePickerOpen}
                            onClose={() => setTemplatePickerOpen(false)}
                            title="Choose Template"
                            width="500px"
                            showActions={false}
                        >
                            <div className="space-y-3">
                                <input
                                    value={templateSearch}
                                    onChange={e => setTemplateSearch(e.target.value)}
                                    placeholder="Search templates..."
                                    className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <div className="space-y-2 max-h-80 overflow-y-auto">
                                    {emailTemplates
                                        .filter(t => t.name.toLowerCase().includes(templateSearch.toLowerCase()))
                                        .map(t => (
                                            <button
                                                key={t.id}
                                                onClick={() => {
                                                    const ctx = {
                                                        customer_name: customerData.customer_name || '',
                                                        company_name: customerData.customer_name || '',
                                                        sales_rep_name: sessionStorage.getItem('login_name') || '',
                                                    };
                                                    const resolveVars = (text) => text.replace(/\{\{(\w+)\}\}/g, (_, k) => ctx[k] || '');
                                                    setEmailForm(p => ({
                                                        ...p,
                                                        subject: resolveVars(t.subject),
                                                        body_html: resolveVars(t.body_html),
                                                    }));
                                                    setTemplatePickerOpen(false);
                                                }}
                                                className="w-full text-left p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-all"
                                            >
                                                <p className="text-sm font-medium text-gray-800 dark:text-gray-100">{t.name}</p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1">{t.subject}</p>
                                            </button>
                                        ))}
                                    {emailTemplates.filter(t => t.name.toLowerCase().includes(templateSearch.toLowerCase())).length === 0 && (
                                        <p className="text-sm text-gray-400 text-center py-4">No templates found</p>
                                    )}
                                </div>
                            </div>
                        </DialogModal>

                        {/* WhatsApp tab */}
                        {activeTab === 'WhatsApp' && (() => {
                            // Build phone options from customer data
                            const phoneOptions = [];
                            if (customerData?.contact_number) {
                                phoneOptions.push({
                                    value: customerData.contact_number,
                                    label: `${customerData.contact_number}${customerData.contact_name ? ` (${customerData.contact_name})` : ''}`,
                                });
                            }
                            (customerData?.contacts || []).forEach(c => {
                                if (c.contact_number && !phoneOptions.find(p => p.value === c.contact_number)) {
                                    phoneOptions.push({
                                        value: c.contact_number,
                                        label: `${c.contact_number}${c.name ? ` (${c.name}${c.designation ? ', ' + c.designation : ''})` : ''}`,
                                    });
                                }
                            });

                            // Auto-select first phone if none selected
                            const effectivePhone = waPhone || (phoneOptions[0]?.value || '');

                            // Parse variable positions from selected template
                            const varPositions = waTemplate
                                ? (() => { try { return JSON.parse(waTemplate.variable_names || '[]'); } catch { return []; } })()
                                : [];

                            const handleWaSend = async () => {
                                if (!waAccounts[0]) { ToastMsg('error', 'No WhatsApp account connected'); return; }
                                if (!effectivePhone) { ToastMsg('error', 'No phone number for this customer'); return; }
                                if (waTemplate && varPositions.some(p => !(waTemplateVars[p] || '').trim())) {
                                    ToastMsg('error', 'Please fill in all template variables'); return;
                                }
                                setWaSending(true);
                                const payload = {
                                    account_id: waAccounts[0].id,
                                    to_phone: effectivePhone,
                                    customer_id: customerId,
                                    customer_name: customerData.customer_name || '',
                                    message_type: waTemplate ? 'template' : 'text',
                                    body_text: waText.trim(),
                                    template_id: waTemplate?.id || null,
                                    template_variables: waTemplateVars,
                                };
                                const res = await create(WA_SEND_URL, payload);
                                setWaSending(false);
                                if (res?.status === 'success') {
                                    ToastMsg('success', 'WhatsApp message sent');
                                    setWaText('');
                                    setWaTemplate(null);
                                    setWaTemplateVars({});
                                    fetch(`${WA_MESSAGES_URL}?customer_id=${customerId}`).then(r => {
                                        if (r?.status === 'success') setWaMessages(r.data || []);
                                    });
                                } else {
                                    ToastMsg('error', res?.message || 'Failed to send');
                                }
                            };

                            return (
                                <div className="space-y-4">
                                    {/* Compose */}
                                    <div className="p-4 bg-green-50 dark:bg-green-900/10 rounded-xl border border-green-200 dark:border-green-800">
                                        <p className="text-xs font-medium text-green-700 dark:text-green-400 mb-2">Send WhatsApp Message</p>
                                        {waAccounts.length === 0 ? (
                                            <p className="text-sm text-gray-400">No WhatsApp accounts connected. Go to Settings → WhatsApp Integration.</p>
                                        ) : phoneOptions.length === 0 ? (
                                            <p className="text-sm text-gray-400">No phone number on this customer. Add a contact with a phone number first.</p>
                                        ) : (
                                            <>
                                                {/* Phone selector + verify */}
                                                <div className="flex items-center gap-2 mb-3">
                                                    <div className="flex-1">
                                                        <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-0.5 block">To</label>
                                                        <select
                                                            value={effectivePhone}
                                                            onChange={e => setWaPhone(e.target.value)}
                                                            className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500"
                                                        >
                                                            {phoneOptions.map(o => (
                                                                <option key={o.value} value={o.value}>{o.label}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>

                                                {/* 24h window info */}
                                                <div className="mb-2 text-[10px] text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-800/50 rounded px-2 py-1.5">
                                                    <strong className="text-gray-500">Free-text</strong> works only if the customer messaged you within the last 24 hours.
                                                    Outside that window, use a <strong className="text-gray-500">template</strong>.
                                                </div>

                                                {/* Template selector — optional */}
                                                <div className="mb-2">
                                                    <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-0.5 block">
                                                        Template <span className="text-gray-400">(optional — required outside 24h window)</span>
                                                    </label>
                                                    <Select
                                                        className="w-full"
                                                        placeholder="Select a template or type a free-text message below"
                                                        allowClear
                                                        value={waTemplate?.id}
                                                        options={waTemplates.filter(t => !t.is_disabled_in_crm && (!t.account_id || t.account_id === waAccounts[0]?.id)).map(t => ({ value: t.id, label: t.template_name }))}
                                                        onChange={v => {
                                                            const t = waTemplates.find(x => x.id === v) || null;
                                                            setWaTemplate(t);
                                                            if (t) {
                                                                const positions = (() => { try { return JSON.parse(t.variable_names || '[]'); } catch { return []; } })();
                                                                const prefilled = {};
                                                                positions.forEach((p, i) => {
                                                                    if (i === 0) prefilled[p] = customerData.customer_name || '';
                                                                });
                                                                setWaTemplateVars(prefilled);
                                                                setWaText('');
                                                            } else {
                                                                setWaTemplateVars({});
                                                            }
                                                        }}
                                                    />
                                                </div>

                                                {/* Template variable inputs + preview */}
                                                {waTemplate && (
                                                    <div className="mb-2 space-y-1.5">
                                                        {varPositions.map(pos => (
                                                            <div key={pos} className="flex items-center gap-2">
                                                                <span className="text-[10px] text-gray-500 w-8 shrink-0">{`{{${pos}}}`}</span>
                                                                <input
                                                                    className="flex-1 border border-gray-200 dark:border-gray-700 rounded px-2 py-1 text-xs bg-white dark:bg-gray-800 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-green-500"
                                                                    placeholder={`Variable ${pos}`}
                                                                    value={waTemplateVars[pos] || ''}
                                                                    onChange={e => setWaTemplateVars(prev => ({ ...prev, [pos]: e.target.value }))}
                                                                />
                                                            </div>
                                                        ))}
                                                        <div className="text-xs text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 rounded p-2 whitespace-pre-wrap">
                                                            {waTemplate.body_text.replace(/\{\{(\d+)\}\}/g, (_, p) => waTemplateVars[p] || `{{${p}}}`)}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Free-text message — always visible */}
                                                {!waTemplate && (
                                                    <textarea
                                                        className="w-full border border-gray-200 dark:border-gray-700 rounded-lg p-2 text-sm bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 resize-none focus:outline-none focus:ring-2 focus:ring-green-500 mb-2"
                                                        rows={2}
                                                        placeholder="Type your message..."
                                                        value={waText}
                                                        onChange={e => setWaText(e.target.value)}
                                                    />
                                                )}

                                                <Button
                                                    onClick={handleWaSend}
                                                    disabled={waSending || (!waText.trim() && !waTemplate)}
                                                    className="w-full flex items-center gap-2 justify-center bg-green-500 hover:bg-green-600 text-sm"
                                                >
                                                    {waSending ? <span className="animate-spin">⟳</span> : <Send className="h-4 w-4" />}
                                                    Send
                                                </Button>
                                            </>
                                        )}
                                    </div>

                                    {/* Message list */}
                                    {waMessages.length === 0 ? (
                                        <p className="text-sm text-gray-400 text-center py-6">No WhatsApp messages yet.</p>
                                    ) : (
                                        <div className="space-y-2">
                                            {waMessages.map(m => {
                                                const isSent = m.direction === 'sent';
                                                return (
                                                    <div key={m.id} className={cn("flex", isSent ? "justify-end" : "justify-start")}>
                                                        <div className={cn(
                                                            "max-w-sm px-3 py-2 rounded-2xl text-sm shadow-sm",
                                                            isSent
                                                                ? "bg-[#dcf8c6] dark:bg-green-900/40 rounded-tr-none"
                                                                : "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-tl-none"
                                                        )}>
                                                            <p className="text-gray-800 dark:text-gray-100 whitespace-pre-wrap">
                                                                {m.body_text || (m.template_name ? `[Template: ${m.template_name}]` : `[${m.message_type}]`)}
                                                            </p>
                                                            <div className="flex items-center gap-1 mt-1 justify-end">
                                                                <span className="text-[10px] text-gray-400">
                                                                    {m.sent_at ? new Date(m.sent_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                                                                </span>
                                                                <span className={cn(
                                                                    "text-[10px] font-medium px-1.5 rounded",
                                                                    m.status === 'read' ? "text-blue-500" :
                                                                    m.status === 'delivered' ? "text-teal-500" :
                                                                    m.status === 'sent' ? "text-gray-400" :
                                                                    m.status === 'failed' ? "text-red-400" : "text-gray-300"
                                                                )}>
                                                                    {m.status}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })()}

                        {/* Unified date-grouped timeline — every tab except the
                            ones that render their own panel instead. */}
                        {TIMELINE_TABS.includes(activeTab) && (() => {
                            const allLogs = customerData.status_log || [];
                            // For All Activities, merge tasks, emails and WA messages as synthetic log entries
                            const taskLogs = activeTab === 'All Activities'
                                ? customerTasks.map(t => ({ ...t, _isTask: true, created_date: t.created_date }))
                                : [];
                            const emailLogs = activeTab === 'All Activities' && hasFeature('email_integration')
                                ? emails.map(e => ({ ...e, _isEmail: true, created_date: e.sent_at || e.created_date }))
                                : [];
                            const waLogs = activeTab === 'All Activities' && (hasFeature('whatsapp_campaigns') || hasFeature('whatsapp_inbox'))
                                ? waMessages.map(m => ({ ...m, _isWhatsApp: true, created_date: m.sent_at || m.created_date }))
                                : [];
                            const filtered = [
                                ...(
                                    activeTab === 'All Activities' ? allLogs :
                                    activeTab === 'CRM Events'    ? allLogs.filter(l => (l.from_status || l.to_status) && l.log_type !== 'note') :
                                    activeTab === 'Notes'         ? allLogs.filter(l => l.log_type === 'note') :
                                    activeTab === 'Calls'         ? allLogs.filter(l => l.log_type === 'call') :
                                    activeTab === 'Documents'     ? allLogs.filter(l => l.log_type === 'document') :
                                    allLogs
                                ),
                                ...taskLogs,
                                ...emailLogs,
                                ...waLogs,
                            ];

                            if (filtered.length === 0) return (
                                <div className="text-center py-8">
                                    <div className="w-14 h-14 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 rounded-xl flex items-center justify-center mx-auto mb-3">
                                        <Clock className="h-6 w-6 text-gray-400 dark:text-gray-500" />
                                    </div>
                                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No {activeTab} yet</p>
                                </div>
                            );

                            // Group by date (newest date first)
                            const groups = {};
                            // Sort all entries newest first before grouping
                            const sortedFiltered = [...filtered].sort((a, b) =>
                                dayjs(b.created_date || b.modified_date).valueOf() -
                                dayjs(a.created_date || a.modified_date).valueOf()
                            );
                            sortedFiltered.forEach(log => {
                                const dateKey = dayjs(log.created_date || log.modified_date).format('YYYY-MM-DD');
                                if (!groups[dateKey]) groups[dateKey] = [];
                                groups[dateKey].push(log);
                            });
                            const sortedGroups = Object.entries(groups).sort(([a], [b]) => b.localeCompare(a));

                            const getDateLabel = (dateKey) => {
                                const today = dayjs().format('YYYY-MM-DD');
                                const yesterday = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
                                if (dateKey === today) return 'Today';
                                if (dateKey === yesterday) return 'Yesterday';
                                return dayjs(dateKey).format('DD MMM YYYY');
                            };

                            return (
                                <div className="space-y-6">
                                    {sortedGroups.map(([dateKey, groupLogs]) => (
                                        <div key={dateKey}>
                                            {/* Date separator */}
                                            <div className="flex items-center gap-3 mb-4">
                                                <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
                                                <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-3 py-1 bg-gray-100 dark:bg-gray-800 rounded-full border border-gray-200 dark:border-gray-700">
                                                    {getDateLabel(dateKey)}
                                                </span>
                                                <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
                                            </div>

                                            {/* Logs for this date */}
                                            <div className="space-y-3">
                                                {groupLogs.map((log, index) => {
                                                    const isLast = index === groupLogs.length - 1;
                                                    const shouldTruncate = log.remarks && log.remarks.length > 100;

                                                    // Task card
                                                    if (log._isTask) return (
                                                        <div key={`task-${log.id}`} className="relative pl-10">
                                                            {!isLast && <div className="absolute left-5 top-8 w-0.5 h-full bg-gradient-to-b from-blue-200 to-gray-200 dark:from-blue-800 dark:to-gray-700" />}
                                                            <div className="absolute left-0 top-1 w-8 h-8 rounded-full border-4 border-white dark:border-gray-800 flex items-center justify-center z-10 shadow-sm bg-blue-500">
                                                                <CheckSquare className="h-3.5 w-3.5 text-white" />
                                                            </div>
                                                            <div className="p-3 rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/10">
                                                                <div className="flex items-start justify-between mb-1.5">
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="text-xs font-semibold text-blue-700 dark:text-blue-300">Task</span>
                                                                        <span className={cn("px-1.5 py-0.5 text-[10px] font-medium rounded-full",
                                                                            log.priority === 'High' ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' :
                                                                            log.priority === 'Medium' ? 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400' :
                                                                            'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                                                                        )}>{log.priority}</span>
                                                                        <span className={cn("px-1.5 py-0.5 text-[10px] font-medium rounded-full",
                                                                            log.task_status === 'Completed' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                                                            log.task_status === 'In Progress' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                                                                            'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                                                                        )}>{log.task_status}</span>
                                                                    </div>
                                                                    <span className="text-[10px] text-gray-400">{formatDateTime(log.created_date)}</span>
                                                                </div>
                                                                <p className={cn("text-sm font-medium text-gray-800 dark:text-gray-200",
                                                                    log.task_status === 'Completed' && "line-through text-gray-400"
                                                                )}>{log.title}</p>
                                                                <div className="flex items-center gap-3 mt-1.5 text-[0.60rem] text-gray-500 dark:text-gray-400">
                                                                    {log.assigned_to_name && <span>→ {log.assigned_to_name}</span>}
                                                                    {log.due_date && <span>Due: {dayjs(log.due_date).format('DD MMM YYYY')}</span>}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );

                                                    // Email card
                                                    if (log._isEmail) return (
                                                        <div key={`email-${log.id}`} className="relative pl-10">
                                                            {!isLast && <div className="absolute left-5 top-8 w-0.5 h-full bg-gradient-to-b from-indigo-200 to-gray-200 dark:from-indigo-800 dark:to-gray-700" />}
                                                            <div className="absolute left-0 top-1 w-8 h-8 rounded-full border-4 border-white dark:border-gray-800 flex items-center justify-center z-10 shadow-sm bg-indigo-500">
                                                                <Mail className="h-3.5 w-3.5 text-white" />
                                                            </div>
                                                            <div className="p-3 rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/10">
                                                                <div className="flex items-start justify-between mb-1">
                                                                    <div className="flex items-center gap-2 flex-wrap">
                                                                        <span className="text-xs font-semibold text-indigo-700 dark:text-indigo-300">
                                                                            {log.direction === 'sent' ? 'Email Sent' : 'Email Received'}
                                                                        </span>
                                                                        {log.is_opened && <span className="px-1.5 py-0.5 text-[9px] font-medium rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">Opened</span>}
                                                                        {log.is_clicked && <span className="px-1.5 py-0.5 text-[9px] font-medium rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">Clicked</span>}
                                                                        {log.is_bounced && <span className="px-1.5 py-0.5 text-[9px] font-medium rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">Bounced</span>}
                                                                    </div>
                                                                    <span className="text-[10px] text-gray-400">{log.sent_at ? dayjs(log.sent_at).format('HH:mm') : ''}</span>
                                                                </div>
                                                                <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{log.subject}</p>
                                                                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                                                                    {log.direction === 'sent' ? `To: ${log.to_email}` : `From: ${log.from_email}`}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    );

                                                    // WhatsApp card
                                                    if (log._isWhatsApp) return (
                                                        <div key={`wa-${log.id}`} className="relative pl-10">
                                                            {!isLast && <div className="absolute left-5 top-8 w-0.5 h-full bg-gradient-to-b from-green-200 to-gray-200 dark:from-green-800 dark:to-gray-700" />}
                                                            <div className="absolute left-0 top-1 w-8 h-8 rounded-full border-4 border-white dark:border-gray-800 flex items-center justify-center z-10 shadow-sm bg-green-500">
                                                                <MessageSquare className="h-3.5 w-3.5 text-white" />
                                                            </div>
                                                            <div className="p-3 rounded-xl border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/10">
                                                                <div className="flex items-start justify-between mb-1">
                                                                    <span className="text-xs font-semibold text-green-700 dark:text-green-300">
                                                                        {log.direction === 'sent' ? 'WhatsApp Sent' : 'WhatsApp Received'}
                                                                    </span>
                                                                    <span className="text-[10px] text-gray-400">{log.sent_at ? dayjs(log.sent_at).format('HH:mm') : ''}</span>
                                                                </div>
                                                                <p className="text-sm text-gray-800 dark:text-gray-200">
                                                                    {log.body_text || (log.template_name ? `[${log.template_name}]` : '')}
                                                                </p>
                                                                {log.sent_by_name && <p className="text-[11px] text-gray-500 mt-0.5">By: {log.sent_by_name}</p>}
                                                            </div>
                                                        </div>
                                                    );

                                                    // Note card
                                                    if (log.log_type === 'note') return (
                                                        <div key={log.id || index} className="relative pl-10">
                                                            {!isLast && <div className="absolute left-5 top-8 w-0.5 h-full bg-gradient-to-b from-yellow-200 to-gray-200 dark:from-yellow-800 dark:to-gray-700" />}
                                                            <div className="absolute left-0 top-1 w-8 h-8 rounded-full border-4 border-white dark:border-gray-800 flex items-center justify-center z-10 shadow-sm bg-yellow-400">
                                                                <MessageSquare className="h-3.5 w-3.5 text-white" />
                                                            </div>
                                                            <div className="p-3 rounded-xl border border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/10">
                                                                <div className="flex items-start justify-between mb-1.5">
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="text-xs font-semibold text-yellow-700 dark:text-yellow-300">Note</span>
                                                                        <span className="text-[10px] text-gray-400">{formatDateTime(log.created_date)}</span>
                                                                    </div>
                                                                    <button
                                                                        onClick={() => { setEditingNoteId(log.id); setEditingNoteText(log.remarks || ''); }}
                                                                        className="h-6 px-2 text-[10px] font-medium border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-500 flex items-center gap-1">
                                                                        <Edit3 className="h-3 w-3" />Edit
                                                                    </button>
                                                                </div>
                                                                {editingNoteId === log.id ? (
                                                                    <div className="space-y-2">
                                                                        <Textarea
                                                                            value={editingNoteText}
                                                                            onChange={(e) => setEditingNoteText(e.target.value)}
                                                                            rows={3}
                                                                            className="w-full rounded-lg border border-yellow-300 dark:border-yellow-700 bg-white dark:bg-gray-800 resize-none text-sm"
                                                                        />
                                                                        <div className="flex gap-2">
                                                                            <button onClick={() => handleSaveEditNote(log.id)}
                                                                                className="h-6 px-2.5 text-[10px] font-medium bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-1">
                                                                                <Check className="h-3 w-3" />Save
                                                                            </button>
                                                                            <button onClick={() => setEditingNoteId(null)}
                                                                                className="h-6 px-2.5 text-[10px] font-medium border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400">
                                                                                Cancel
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">{log.remarks}</p>
                                                                )}
                                                                <div className="flex items-center gap-1.5 mt-2 text-[0.60rem] text-gray-500 dark:text-gray-400">
                                                                    <User className="h-3 w-3" />
                                                                    <span className="font-medium">{log.user_name}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );

                                                    // CRM event / status card
                                                    return (
                                                        <div key={log.id || index} className="relative pl-10">
                                                            {!isLast && <div className="absolute left-5 top-8 w-0.5 h-full bg-gradient-to-b from-orange-200 to-gray-200 dark:from-orange-800 dark:to-gray-700" />}
                                                            <div className={cn(
                                                                "absolute left-0 top-1 w-8 h-8 rounded-full border-4 border-white dark:border-gray-800 flex items-center justify-center z-10 shadow-sm",
                                                                getTimelineDotColor(log.to_status)
                                                            )}>
                                                                {getStatusIcon(log.to_status)}
                                                            </div>
                                                            <div className={cn(
                                                                "p-4 rounded-xl border-2 transition-all duration-300 hover:shadow-md",
                                                                getTimelineCardColor(log.to_status)
                                                            )}>
                                                                <div className="flex items-start justify-between mb-3">
                                                                    <div className="flex items-center gap-2">
                                                                        <div className={cn("w-2 h-2 rounded-full animate-pulse", getStatusDotColor(log.to_status))} />
                                                                        <span className={cn("font-bold text-sm", getStatusTextColor(log.to_status))}>
                                                                            {log.to_status || log.from_status}
                                                                        </span>
                                                                    </div>
                                                                    <span className="text-[0.50rem] text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 px-2 py-1 rounded-full font-medium">
                                                                        {formatDateTime(log.created_date || log.modified_date)}
                                                                    </span>
                                                                </div>
                                                                {log.from_status && log.to_status && (
                                                                    <div className="flex items-center gap-2 mb-3 p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-600">
                                                                        <span className="text-xs text-gray-600 dark:text-gray-400 font-medium px-2 py-1 bg-gray-50 dark:bg-gray-700 rounded">{log.from_status}</span>
                                                                        <ArrowRight className="h-3 w-3 text-gray-400" />
                                                                        <span className={cn("text-xs font-bold px-2 py-1 rounded", getStatusTextColor(log.to_status))}>{log.to_status}</span>
                                                                    </div>
                                                                )}
                                                                {log.remarks && (
                                                                    <div className="mb-3">
                                                                        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">{log.remarks}</p>
                                                                    </div>
                                                                )}
                                                                <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-600">
                                                                    <div>
                                                                        {log.next_followup_date && (
                                                                            <div className="flex items-center gap-1.5 text-[0.70rem] text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-2.5 py-1 rounded-lg">
                                                                                <Calendar className="h-3 w-3" />
                                                                                <span className="font-medium">Follow-up: {formatDateTime(log.next_followup_date)}</span>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <div className="flex items-center gap-1.5 text-[0.60rem] text-gray-500 dark:text-gray-400">
                                                                        <User className="h-3 w-3" />
                                                                        <span className="font-medium">{log.user_name}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}

                                </div>
                            );
                        })()}
                        </div>
                    </div>
                </div>

                {/* ── Context panel ── */}
                <aside className="space-y-3 lg:sticky lg:top-3" aria-label="Customer context">
                    {/* 1. Customer Information — first in the panel: name, stage, owner and source are what a rep looks for first */}
                    <div ref={detailsRef} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 scroll-mt-4">
                        {/* Top row: back + label + funnel + edit */}
                        <div className="flex items-center gap-1.5 mb-3">
                            <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest flex-1">Details</p>
                            {editingCard === 'info' ? (
                                <div className="flex gap-1 flex-shrink-0">
                                    <button onClick={() => { handleSaveInfoCard(); setEditingCard(null); }}
                                        className="h-6 px-2.5 text-[10px] font-medium bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-1">
                                        <Check className="h-3 w-3" />Save
                                    </button>
                                    <button onClick={() => { handleCancelEdit(); setEditingCard(null); }}
                                        className="h-6 px-2.5 text-[10px] font-medium border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400">
                                        Cancel
                                    </button>
                                </div>
                            ) : (
                                <div className="flex gap-1 flex-shrink-0">
                                    <button onClick={() => { setEditingCard('info'); setIsEditMode(true); }}
                                        className="h-6 px-2 text-[10px] font-medium border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 flex items-center gap-1">
                                        <Edit className="h-3 w-3" />Edit
                                    </button>
                                </div>
                            )}
                        </div>
                        {editingCard === 'info' ? (
                            <div className="space-y-3">
                                <EditableInfoItem label="Customer Name" value={formData.customer_name} onChange={v => handleInputChange('customer_name', v)} />
                                <EditableInfoItem label="Customer Type" type="select" value={formData.customer_type} onChange={v => handleInputChange('customer_type', v)}
                                    options={customerTypeOptions} />
                                <EditableInfoItem label="Address" value={formData.address} onChange={v => handleInputChange('address', v)} />
                                <LocationSelects size="middle" columns={1}
                                    labelClassName="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide"
                                    value={{ country: formData.country, state: formData.state, city: formData.city }}
                                    onChange={loc => setFormData(prev => ({ ...prev, ...loc }))} />
                                <EditableInfoItem label="Lead Potential" type="select" value={formData.potential} onChange={v => handleInputChange('potential', v)}
                                    options={[{value:'Hot',label:'🔥 Hot'},{value:'Medium',label:'⚡ Medium'},{value:'Cold',label:'❄️ Cold'}]} />
                                {!isChannelPartner && (
                                    <EditableInfoItem label="Lead Source" type="select" value={formData.lead_source || undefined}
                                        onChange={v => {
                                            handleInputChange('lead_source', v);
                                            // Another source means no partner. Left set, it would keep
                                            // counting towards that partner's leaderboard row while the
                                            // lead itself claimed to come from somewhere else.
                                            if (!isPartnerSource(v)) handleInputChange('sourced_by_partner', null);
                                        }}
                                        options={LEAD_SOURCE_OPTIONS} />
                                )}
                                {!isChannelPartner && isPartnerSource(formData.lead_source) && (
                                    <EditableInfoItem label="Sourced By (Channel Partner)" type="select"
                                        value={formData.sourced_by_partner || undefined}
                                        onChange={v => handleInputChange('sourced_by_partner', v)}
                                        options={partnerOptions} />
                                )}
                                {projectAssignOn && (
                                    <EditableInfoItem label="Project" type="select"
                                        value={formData.project_id || undefined}
                                        onChange={v => handleInputChange('project_id', v)}
                                        options={projectOptions.map(p => ({ value: p.id, label: p.name }))} />
                                )}
                                {projectAssignOn && (() => {
                                    // A lead is not "also" in its main project. Links into
                                    // projects this user cannot open keep their name.
                                    const known = [...projectOptions, ...(customerData.extra_projects || [])
                                        .filter(k => !projectOptions.some(p => p.id === k.id))];
                                    return (
                                        <div className="flex flex-col">
                                            <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">Also in projects</span>
                                            <Select mode="multiple" allowClear size="large" className="w-full" placeholder="None"
                                                value={(formData.extra_project_ids || []).filter(id => id !== formData.project_id)}
                                                onChange={v => handleInputChange('extra_project_ids', v)}
                                                options={known.filter(p => p.id !== formData.project_id)
                                                    .map(p => ({ value: p.id, label: p.name }))} />
                                        </div>
                                    );
                                })()}
                                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Active Status</span>
                                    <button
                                        type="button"
                                        onClick={() => handleInputChange('is_active', !formData.is_active)}
                                        className={cn("relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200",
                                            formData.is_active ? "bg-green-500" : "bg-gray-300 dark:bg-gray-600")}
                                    >
                                        <span className={cn("inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 shadow-lg",
                                            formData.is_active ? "translate-x-6" : "translate-x-1")} />
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {/* Key fields — always show all rows */}
                                <div className="space-y-2 text-xs">
                                    <div className="flex items-start gap-2 text-gray-600 dark:text-gray-400">
                                        <MapPin className="h-3.5 w-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                                        <span className="break-words">
                                            {[customerData.address, customerData.city, stateName(customerData.country || DEFAULT_COUNTRY, customerData.state), customerData.country].filter(Boolean).join(', ') || <span className="text-gray-300 dark:text-gray-600 italic">No address</span>}
                                        </span>
                                    </div>
                                    {(() => {
                                        const pc = customerData.contacts?.find(c => c.is_primary_contact) || customerData.contacts?.[0];
                                        return (
                                            <>
                                                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                                    <User className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                                                    <span>{pc?.name || customerData.contact_name || <span className="text-gray-300 dark:text-gray-600 italic">No contact</span>}</span>
                                                </div>
                                                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                                    <Phone className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                                                    <span>{pc?.contact_number || customerData.contact_number || <span className="text-gray-300 dark:text-gray-600 italic">No phone</span>}</span>
                                                    {customerId && (pc?.contact_number || customerData.contact_number) && (
                                                        <CallButton size="xs" customerId={customerId}
                                                            number={pc?.contact_number || customerData.contact_number}
                                                            onCalled={() => { onStaffCalled(); setActiveTab('Calls'); }} />
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                                    <Mail className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                                                    <span className="truncate">{pc?.email || <span className="text-gray-300 dark:text-gray-600 italic">No email</span>}</span>
                                                </div>
                                            </>
                                        );
                                    })()}
                                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                        <Briefcase className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                                        <span>{customerData.current_assignee || customerData.assigned_to_name || <span className="text-gray-300 dark:text-gray-600 italic">Unassigned</span>}</span>
                                        <button type="button" onClick={() => setOwnerHistoryOpen(true)}
                                            className="text-[11px] text-primary hover:underline" title="Why this owner, and every owner change">Why?</button>
                                    </div>
                                    {customerData.lead_source && (
                                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                            <TrendingUp className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                                            <span>{customerData.lead_source}</span>
                                        </div>
                                    )}
                                    {customerData.sourced_by_partner_name && (
                                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                            <Handshake className="h-3.5 w-3.5 text-purple-500 flex-shrink-0" />
                                            <button
                                                type="button"
                                                onClick={() => navigate(`/customers/customerdetails?id=${customerData.sourced_by_partner}`)}
                                                className="text-purple-600 dark:text-purple-400 hover:underline truncate text-left"
                                                title="Open this channel partner"
                                            >
                                                {customerData.sourced_by_partner_name}
                                            </button>
                                        </div>
                                    )}
                                    {projectAssignOn && (
                                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400" title="Project">
                                            <FolderKanban className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                                            <span>
                                                {customerData.project_name || <span className="text-gray-300 dark:text-gray-600 italic">No project</span>}
                                                {customerData.extra_projects?.length > 0 && (
                                                    <span className="text-gray-400"> · also in {customerData.extra_projects.map(p => p.name).join(', ')}</span>
                                                )}
                                            </span>
                                        </div>
                                    )}
                                    {customerData.created_date && (
                                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                            <Calendar className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                                            <span className="text-[11px]">Created: {formatDateTime(customerData.created_date)}</span>
                                        </div>
                                    )}
                                    {(customerData.modified_date || customerData.last_update_date) && (
                                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                            <Clock className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                                            <span className="text-[11px]">Updated: {dayjs(customerData.modified_date || customerData.last_update_date).format('DD MMM YYYY')}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    {customerId && <DemoSlotsCard customerId={customerId} />}
                    {customerId && <NurtureCard customerId={customerId} stage={customerData.lead_stage} onRevive={reviveFromNurture} />}
                    {customerId && <DemoReminderCard customerId={customerId} stage={customerData.lead_stage} />}
                    {customerId && <DemoFeedbackCard customerId={customerId} stage={customerData.lead_stage} />}
                    <div className="hidden lg:block">{nextFollowUpCard}</div>
                    {customerId && <LeadAISuggestionPanel customerId={customerId} />}
                    {/* 3. Estimated Deal Value — hidden for Channel Partners */}
                    {!isChannelPartner && <div className={cn("rounded-xl p-4",
                        editingCard === 'deal' ? "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700" : "bg-emerald-600 dark:bg-emerald-700")}>
                        <div className="flex items-center justify-between mb-4">
                            <p className={cn("text-[10px] font-bold uppercase tracking-widest",
                                editingCard === 'deal' ? "text-gray-400 dark:text-gray-500" : "text-emerald-200")}>Estimated Deal Value</p>
                            {editingCard === 'deal' ? (
                                <div className="flex gap-1">
                                    <button onClick={() => { handleSaveDealCard(); setEditingCard(null); }}
                                        className="h-6 px-2.5 text-[10px] font-medium bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-1">
                                        <Check className="h-3 w-3" />Save
                                    </button>
                                    <button onClick={() => { handleCancelEdit(); setEditingCard(null); }}
                                        className="h-6 px-2.5 text-[10px] font-medium border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400">
                                        Cancel
                                    </button>
                                </div>
                            ) : (
                                <button onClick={() => { setEditingCard('deal'); setIsEditMode(true); }}
                                    className="h-6 px-2 text-[10px] font-medium border border-emerald-400/50 rounded-md hover:bg-emerald-500 text-emerald-100 flex items-center gap-1">
                                    <Edit className="h-3 w-3" />Edit
                                </button>
                            )}
                        </div>
                        {editingCard === 'deal' ? (
                            <div className="space-y-3">
                                <EditableInfoItem label="Proposed Value (₹)" value={formData.proposed_value} onChange={v => handleInputChange('proposed_value', v)} type="number" prefix="₹" />
                                <EditableInfoItem label="Discount (%)" value={formData.discount_offered} onChange={v => handleInputChange('discount_offered', v)} type="number" suffix="%" />
                                <div>
                                    <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 block">Final Price (auto-calc)</span>
                                    <div className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                                        ₹{Number(formData.final_quoted_price || 0).toLocaleString()}
                                    </div>
                                </div>
                                <EditableInfoItem label="Expected Closing Date" value={formData.expected_closing_date} onChange={v => handleInputChange('expected_closing_date', v)} type="date" />
                            </div>
                        ) : (
                            <div className="text-white">
                                <div className="text-2xl font-bold">
                                    ₹{Number(customerData.financials?.proposed_value || 0).toLocaleString()}
                                </div>
                                <p className="text-emerald-200 text-xs mt-0.5">Proposed Value</p>
                                <div className="mt-4 space-y-2 border-t border-emerald-500/50 pt-3">
                                    <div className="flex justify-between text-xs">
                                        <span className="text-emerald-200">Discount</span>
                                        <span className="font-semibold">{customerData.financials?.discount_offered || 0}%</span>
                                    </div>
                                    <div className="flex justify-between text-xs">
                                        <span className="text-emerald-200">Final Price</span>
                                        <span className="font-bold">₹{Number(customerData.financials?.final_quoted_price || 0).toLocaleString()}</span>
                                    </div>
                                    {customerData.followup?.next_followup_date && (
                                        <div className="flex justify-between text-xs">
                                            <span className="text-emerald-200">Next Follow-up</span>
                                            <span>{formatDateTime(customerData.followup.next_followup_date)}</span>
                                        </div>
                                    )}
                                    {customerData.financials?.expected_closing_date && (
                                        <div className="flex justify-between text-xs">
                                            <span className="text-emerald-200">Expected Close</span>
                                            <span className="font-semibold">{formatDate(customerData.financials.expected_closing_date)}</span>
                                        </div>
                                    )}
                                </div>
                                {/* Deal History */}
                                {customerData.financials_history?.length > 0 && (
                                    <div className="mt-3 border-t border-emerald-500/50 pt-3">
                                        <button
                                            onClick={() => setShowDealHistory(prev => !prev)}
                                            className="text-[10px] font-medium text-emerald-200 hover:text-white flex items-center gap-1"
                                        >
                                            <svg className={cn("h-3 w-3 transition-transform", showDealHistory && "rotate-90")} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                            </svg>
                                            Deal History ({customerData.financials_history.length})
                                        </button>
                                        {showDealHistory && (
                                            <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                                                {customerData.financials_history.map((h, i) => (
                                                    <div key={h.id || i} className="bg-emerald-500/20 rounded-lg px-3 py-2 text-[10px]">
                                                        <div className="flex justify-between">
                                                            <span className="text-emerald-200">₹{Number(h.final_quoted_price || 0).toLocaleString()}</span>
                                                            <span className="text-emerald-300">{h.expected_closing_date ? formatDate(h.expected_closing_date) : '—'}</span>
                                                        </div>
                                                        <div className="flex justify-between mt-0.5 text-emerald-300/70">
                                                            <span>{h.changed_by || '—'}</span>
                                                            <span>{h.created_at ? new Date(h.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>}
                    {/* 1b. Final Quotation (shown once a plan is frozen) */}
                    {frozenQuote?.accepted_plan && (
                        <div className="bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20 rounded-xl border-2 border-emerald-300 dark:border-emerald-700 p-4 shadow-sm">
                            <div className="flex items-center gap-1.5 mb-3">
                                <div className="w-6 h-6 bg-emerald-600 rounded-md flex items-center justify-center flex-shrink-0">
                                    <CheckCircle className="h-3.5 w-3.5 text-white" />
                                </div>
                                <p className="text-[10px] font-bold text-emerald-700 dark:text-emerald-300 uppercase tracking-widest flex-1">Final Quotation</p>
                                <span className="h-5 px-1.5 text-[9px] font-semibold bg-emerald-600 text-white rounded flex items-center">FROZEN</span>
                            </div>
                            <div className="space-y-2 text-xs">
                                <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                                    <FileText className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />
                                    <span className="font-semibold">{frozenQuote.quote_number}</span>
                                    <span className="text-gray-500">·</span>
                                    <span>{frozenQuote.accepted_plan.plan_label}</span>
                                </div>
                                {frozenQuote.accepted_plan.package_title && (
                                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                        <Package className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                                        <span className="truncate">{frozenQuote.accepted_plan.package_title}</span>
                                    </div>
                                )}
                                <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                                    <IndianRupee className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />
                                    <span className="font-bold">₹{Number(frozenQuote.accepted_plan.grand_total || 0).toLocaleString('en-IN')}</span>
                                    <span className="text-gray-500 text-[11px]">(incl. GST)</span>
                                </div>
                                {frozenQuote.accepted_at && (
                                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                        <Calendar className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                                        <span className="text-[11px]">Frozen on {dayjs(frozenQuote.accepted_at).format('DD MMM YYYY')}</span>
                                    </div>
                                )}
                            </div>
                            <div className="flex gap-2 mt-3 pt-3 border-t border-emerald-200 dark:border-emerald-800">
                                <button onClick={() => navigate(`/quotations/${frozenQuote.id}`)}
                                    className="flex-1 h-7 px-2 text-[11px] font-medium border border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 rounded-md hover:bg-emerald-100 dark:hover:bg-emerald-900/30 flex items-center justify-center gap-1">
                                    <FileText className="h-3 w-3" /> View Quote
                                </button>
                                <button onClick={onboardAction}
                                    title={mouBlocks ? 'A signed MOU is required before onboarding' : undefined}
                                    className={`flex-1 h-7 px-2 text-[11px] font-medium text-white rounded-md flex items-center justify-center gap-1 ${
                                        mouBlocks ? 'bg-amber-600 hover:bg-amber-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}>
                                    <BadgeCheck className="h-3 w-3" />
                                    {onboardLabel}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* 2. Key Contacts */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Key Contacts</p>
                            <Button onClick={handleAddContact} size="sm"
                                className="h-7 px-2.5 text-xs bg-primary hover:bg-primary/90 text-white rounded-lg flex items-center gap-1">
                                <Plus className="h-3 w-3" /> Add
                            </Button>
                        </div>
                        {(showAddContact || editingContact) && (
                            <div className="mb-4 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border-2 border-blue-200 dark:border-blue-800 shadow-sm">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-2">
                                        <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center">
                                            {editingContact ? <Edit3 className="h-3.5 w-3.5 text-white" /> : <Plus className="h-3.5 w-3.5 text-white" />}
                                        </div>
                                        <h3 className="font-bold text-gray-900 dark:text-white text-sm">
                                            {editingContact ? 'Edit Contact' : 'Add Contact'}
                                        </h3>
                                    </div>
                                    <Button variant="ghost" size="sm" onClick={handleCancelAddContact} className="h-6 w-6 p-0">
                                        <X className="h-3.5 w-3.5" />
                                    </Button>
                                </div>
                                <div className="space-y-3">
                                    <div>
                                        <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1 block">Full Name *</label>
                                        <Input value={newContact.name} onChange={e => handleNewContactChange('name', e.target.value)} placeholder="Full name" className="h-9 text-sm" autoFocus />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1 block">Designation</label>
                                        <Input value={newContact.designation} onChange={e => handleNewContactChange('designation', e.target.value)} placeholder="Job title" className="h-9 text-sm" />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1 block">Phone *</label>
                                        <Input value={newContact.contact_number} onChange={e => handleNewContactChange('contact_number', e.target.value)} placeholder="Phone number" className="h-9 text-sm" />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1 block">Email</label>
                                        <Input value={newContact.email} onChange={e => handleNewContactChange('email', e.target.value)} placeholder="Email address" type="email" className="h-9 text-sm" />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1 block">Contact Type</label>
                                        <Select value={newContact.type} onChange={v => handleNewContactChange('type', v)}
                                            options={[{value:'Primary Contact',label:'👑 Primary'},{value:'Secondary Contact',label:'💼 Secondary'},{value:'Technical Contact',label:'🔧 Technical'},{value:'Billing Contact',label:'💰 Billing'},{value:'Decision Maker',label:'🎯 Decision Maker'}]}
                                            className="w-full" size="large" />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1 block">Department/Role</label>
                                        <Input value={newContact.role} onChange={e => handleNewContactChange('role', e.target.value)} placeholder="Department or role" className="h-9 text-sm" />
                                    </div>
                                </div>
                                <div className="flex gap-2 pt-3 mt-3 border-t border-blue-200 dark:border-blue-700">
                                    <Button onClick={editingContact ? handleUpdateContact : handleSaveContact}
                                        disabled={!newContact.name || !newContact.contact_number}
                                        size="sm" className="bg-primary hover:bg-primary/90 text-white h-8 px-4 text-xs flex-1 flex items-center justify-center gap-1">
                                        <Check className="h-3.5 w-3.5" />
                                        {editingContact ? 'Update' : 'Save'}
                                    </Button>
                                    <Button variant="outline" onClick={handleCancelAddContact} size="sm" className="h-8 px-4 text-xs">Cancel</Button>
                                    {editingContact && (
                                        <Button variant="outline" onClick={() => handleDeleteContact(editingContact)} size="sm"
                                            className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 h-8 px-3 text-xs">
                                            <Trash className="h-3.5 w-3.5" />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )}
                        <div className="space-y-2">
                            {(customerData.contacts || []).map(contact => (
                                <div key={contact.id} className="flex items-center gap-2 p-2.5 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-100 dark:border-gray-600 group hover:border-blue-200 dark:hover:border-blue-700 transition-all">
                                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                                        {contact.name?.charAt(0)?.toUpperCase() || '?'}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-semibold text-gray-900 dark:text-white truncate">{contact.name}</p>
                                        <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">{contact.designation || contact.type}</p>
                                        {contact.contact_number && (
                                            <p className="flex items-center gap-1 text-[11px] text-gray-600 dark:text-gray-300 tabular-nums min-w-0">
                                                <span className="truncate">{contact.contact_number}</span>
                                                <ContactVerify verification={contactVerification} contact={contact} kind="phone" canEdit={canVerifyContacts} />
                                            </p>
                                        )}
                                        {contact.email && (
                                            <p className="flex items-center gap-1 text-[11px] text-gray-600 dark:text-gray-300 min-w-0">
                                                <span className="truncate">{contact.email}</span>
                                                <ContactVerify verification={contactVerification} contact={contact} kind="email" canEdit={canVerifyContacts} />
                                            </p>
                                        )}
                                    </div>
                                    {/* Always visible: hover-only buttons cannot be reached on a touch screen. */}
                                    <div className="flex gap-0.5 flex-shrink-0">
                                        {contact.contact_number && customerId && (
                                            <CallButton size="xs" customerId={customerId} number={contact.contact_number}
                                                onCalled={onCalledFromHeader} />
                                        )}
                                        <button onClick={() => handleEditContact(contact)} aria-label={`Edit ${contact.name || 'contact'}`}
                                            className="h-6 w-6 flex items-center justify-center rounded hover:bg-blue-100 dark:hover:bg-blue-900/30">
                                            <Edit3 className="h-3 w-3 text-blue-500" />
                                        </button>
                                        <button onClick={() => handleDeleteContact(contact)} aria-label={`Remove ${contact.name || 'contact'}`}
                                            className="h-6 w-6 flex items-center justify-center rounded hover:bg-red-100 dark:hover:bg-red-900/20">
                                            <Trash className="h-3 w-3 text-red-500" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                            {(!customerData.contacts || customerData.contacts.length === 0) && !showAddContact && !editingContact && (
                                <div className="text-center py-5 text-gray-400 dark:text-gray-500 text-xs border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
                                    <Users className="h-6 w-6 mx-auto mb-1.5 opacity-40" />
                                    No contacts yet
                                </div>
                            )}
                        </div>
                    </div>

                </aside>
            </div>
            </div>

            <MobileActionBar phone={phones[0]?.number} customerId={customerId} onCalled={onCalledFromHeader}
                onWhatsApp={canWhatsApp ? openWhatsApp : null} onNote={openNote} onStage={() => openStatusModal()} />

            {/* Update stage / follow-up — the status form, unchanged, in a modal */}
            <DialogModal open={statusModalOpen} onClose={() => setStatusModalOpen(false)} showActions={false}
                width="720px" title="Update stage / follow-up" subtitle={customerData.customer_name}>
                <div className="mb-4 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border-2 border-blue-200 dark:border-blue-800">
                                <div className="flex items-center gap-2 flex-wrap mb-3">
                                    <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                                        <TrendingUp className="h-3.5 w-3.5 text-white" />
                                    </div>
                                    <span className="text-sm font-bold text-gray-900 dark:text-white">Update Status</span>
                                    <div className="flex items-center gap-1 text-xs">
                                        <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-gray-600 dark:text-gray-400 font-medium">{statusForm.from_status}</span>
                                        <ArrowRight className="h-3 w-3 text-gray-400" />
                                        <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 rounded text-blue-600 dark:text-blue-400 font-medium">{statusForm.to_status || '—'}</span>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                                    <div>
                                        <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">New Status *</label>
                                        <Select
                                            value={statusForm.to_status}
                                            onChange={(value) => handleStatusFormChange('to_status', value)}
                                            options={leadStageOptions}
                                            className="w-full"
                                            size="large"
                                            placeholder={statusForm.from_status}
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">
                                            {statusForm.to_status === 'Nurture Lead' ? 'Revisit on' : 'Next Follow-up'}
                                            {!NO_FOLLOWUP_STAGES.includes(statusForm.to_status) && <span className="text-red-500 ml-1">*</span>}
                                        </label>
                                        <DatePicker
                                            value={statusForm.next_followup_date ? dayjs(statusForm.next_followup_date) : null}
                                            onChange={(date) => handleStatusFormChange('next_followup_date', date ? date.format('YYYY-MM-DD HH:mm:ss') : null)}
                                            className="w-full"
                                            size="large"
                                            showTime
                                            format="DD MMM YYYY hh:mm A"
                                            placeholder="Select date and time"
                                            suffixIcon={<Calendar className="h-4 w-4 text-gray-400" />}
                                        />
                                    </div>
                                </div>
                                {renderStageFieldsBox()}
                                {DEMO_PRESENTER_STAGES.includes(statusForm.to_status) && (
                                    <DemoPresenterField value={statusForm.demo_by_id}
                                                        onChange={(v) => handleStatusFormChange('demo_by_id', v)} />
                                )}
                                {(statusForm.to_status === 'Demo Fixed' || STALL_REASON_STAGES.includes(statusForm.to_status)) && (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                                        {statusForm.to_status === 'Demo Fixed' && (
                                            <div>
                                                <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">
                                                    Demo Date & Time<span className="text-red-500 ml-1">*</span>
                                                </label>
                                                <DatePicker
                                                    value={statusForm.demo_datetime ? dayjs(statusForm.demo_datetime) : null}
                                                    onChange={(date) => handleStatusFormChange('demo_datetime', date ? date.format('YYYY-MM-DD HH:mm:ss') : null)}
                                                    className="w-full"
                                                    size="large"
                                                    showTime
                                                    format="DD MMM YYYY hh:mm A"
                                                    placeholder="When is the demo scheduled?"
                                                    suffixIcon={<Calendar className="h-4 w-4 text-gray-400" />}
                                                />
                                            </div>
                                        )}
                                        {STALL_REASON_STAGES.includes(statusForm.to_status) && (
                                            <div>
                                                <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">
                                                    Stall Reason<span className="text-red-500 ml-1">*</span>
                                                </label>
                                                <Select
                                                    value={statusForm.stall_reason}
                                                    onChange={(value) => handleStatusFormChange('stall_reason', value)}
                                                    options={STALL_REASON_OPTIONS}
                                                    className="w-full"
                                                    size="large"
                                                    placeholder="Why is this lead stalling?"
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                                    <div>
                                        <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">Channel</label>
                                        <Select
                                            value={statusForm.channel}
                                            onChange={(value) => handleStatusFormChange('channel', value)}
                                            options={CHANNEL_OPTIONS}
                                            className="w-full"
                                            size="large"
                                            allowClear
                                            placeholder="How did you reach out?"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">Contact Outcome</label>
                                        <Select
                                            value={statusForm.contact_outcome}
                                            onChange={(value) => handleStatusFormChange('contact_outcome', value)}
                                            options={CONTACT_OUTCOME_OPTIONS}
                                            className="w-full"
                                            size="large"
                                            allowClear
                                            placeholder="Did you get through?"
                                        />
                                    </div>
                                </div>
                            {/* What Closed Won will record. Shown in the form, not only
                                in the modal, so the rep can see the number they are
                                about to commit before they press save. */}
                            {statusForm.to_status === 'Closed Won' && (
                                <div className={cn(
                                    "rounded-lg border px-3 py-2.5 flex items-center justify-between gap-3 text-sm",
                                    (dealValue?.deal_value || frozenQuote?.accepted_plan)
                                        ? "border-emerald-300 bg-emerald-50/60 dark:bg-emerald-900/15"
                                        : "border-amber-300 bg-amber-50 dark:bg-amber-900/10",
                                )}>
                                    <span className="min-w-0">
                                        {dealValue?.deal_value ? (
                                            <>Deal value{" "}
                                                <strong className="tabular-nums">
                                                    ₹{Number(dealValue.deal_value).toLocaleString('en-IN')}
                                                </strong>
                                                {dealValue.source === 'frozen' ? ' — from the frozen quotation' : ' — estimated'}
                                            </>
                                        ) : frozenQuote?.accepted_plan ? (
                                            <>Deal value{" "}
                                                <strong className="tabular-nums">
                                                    ₹{Number(frozenQuote.accepted_plan.grand_total || 0).toLocaleString('en-IN')}
                                                </strong>
                                                {" "}— from {frozenQuote.quote_number}
                                            </>
                                        ) : (
                                            <span className="text-amber-800 dark:text-amber-300">
                                                A deal value is required before this can be saved.
                                            </span>
                                        )}
                                    </span>
                                    <button type="button" onClick={() => setCloseWonOpen(true)}
                                        className="text-xs font-medium underline shrink-0">
                                        {(dealValue?.deal_value || frozenQuote?.accepted_plan) ? 'Change' : 'Set value'}
                                    </button>
                                </div>
                            )}

                            {statusForm.to_status === 'Closed Won' && handoverStatusBanner}

                                <div className="mb-3">
                                    <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">Remarks & Notes<span className="text-red-500 ml-1">*</span></label>
                                    <Textarea
                                        value={statusForm.remarks}
                                        onChange={(e) => handleStatusFormChange('remarks', e.target.value)}
                                        placeholder="Add remarks about this status change..."
                                        rows={2}
                                        showCharCount={true}
                                        maxLength={500}
                                        className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 resize-none text-sm"
                                    />
                                </div>
                                <div className="flex gap-2">
                                    <Button
                                        onClick={handleStatusUpdate}
                                        disabled={!statusForm.from_status}
                                        size="sm"
                                        className="flex-1 bg-primary hover:bg-primary/90 text-white h-9 text-xs">
                                        <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
                                        Submit Update
                                    </Button>
                                    <Button
                                        variant="outline"
                                        onClick={() => setStatusForm(prev => ({ ...prev, to_status: customerData.lead_stage || prev.from_status, remarks: '', next_followup_date: null, demo_datetime: null, stall_reason: null, channel: null, contact_outcome: null }))}
                                        size="sm"
                                        className="h-9 px-4 text-xs">
                                        Reset
                                    </Button>
                                </div>
                            </div>
            </DialogModal>

            {/* Reassign from the More menu — the same handler the banner uses */}
            <DialogModal open={reassignDialogOpen} onClose={() => { setReassignDialogOpen(false); setReassignToId(null); }}
                onSave={handleReassign} loading={reassignSaving} saveLabel="Reassign" closeLabel="Cancel"
                width="460px" title="Reassign lead" subtitle={`Now owned by ${ownerName || 'nobody'}`}>
                <Select className="w-full" size="large" placeholder="Pick a rep" showSearch optionFilterProp="label"
                    value={reassignToId || undefined} onChange={v => setReassignToId(v)}
                    options={taskUsers.filter(u => u.id !== customerData.assigned_to_id).map(u => ({
                        value: u.id,
                        label: u.login_fname ? `${u.login_fname}${u.login_lname ? ' ' + u.login_lname : ''}` : (u.login_name || `User ${u.id}`),
                    }))} />
            </DialogModal>

            {/* Closed Won deal value — freeze a quotation, or take an estimate */}
            <CloseWonValueModal
                open={closeWonOpen}
                customerId={customerData?.id}
                customerName={customerData?.customer_name}
                onClose={() => setCloseWonOpen(false)}
                onConfirm={(result) => {
                    setDealValue(result);
                    setCloseWonOpen(false);
                    /* A freeze changed the quotation, so the panel showing it is
                       now stale. Refetching keeps the frozen-quote card and the
                       Onboard prefill consistent with what was just agreed. */
                    if (result?.source === 'frozen' && customerData?.id) {
                        refetchCustomerDetails(customerData.id);
                    }
                }}
            />

            {/* Move to Onboarded modal */}
            {showOnboardModal && (
                <AddOnboardedModal
                    customerId={customerData.id}
                    initialData={{
                        org_name:       customerData.customer_name || "",
                        state:          stateCode(customerData.country || DEFAULT_COUNTRY, customerData.state),
                        city:           customerData.city || "",
                        address:        customerData.address || "",
                        contact_name:   customerData.contacts?.find(c => c.is_primary_contact)?.name || customerData.contact_name || "",
                        contact_number: customerData.contacts?.find(c => c.is_primary_contact)?.contact_number || customerData.contact_number || "",
                        contact_email:  customerData.contacts?.find(c => c.is_primary_contact)?.email || "",
                        ...(frozenQuote?.accepted_plan ? {
                            product_sold:      frozenQuote.accepted_plan.package_title || frozenQuote.accepted_plan.plan_label || "",
                            contract_value:    frozenQuote.accepted_plan.grand_total || "",
                            remarks:           `From quotation ${frozenQuote.quote_number} (plan: ${frozenQuote.accepted_plan.plan_label}).`,
                            quotation_plan_id: frozenQuote.accepted_plan.id,
                            amc_percentage:    frozenQuote.accepted_plan.amc_rate || "",
                        } : {}),
                    }}
                    users={taskUsers}
                    onClose={() => setShowOnboardModal(false)}
                    onSuccess={(id) => {
                        setShowOnboardModal(false);
                        navigate(`/onboarded/${id}`);
                    }}
                />
            )}
        </ContainerCard>
    );
};

// Reusable Section Component with Add Button
// Status Badge Component
// Contact Card Component
// Reusable Info Item Component
// Metric Card Component
// Editable Components
const EditableInfoItem = ({ label, value, onChange, type = "text", options = [], prefix, suffix, max }) => {
    const renderInput = () => {
        switch (type) {
            case "select":
                return (
                    <Select
                        value={value}
                        onChange={onChange}
                        options={options}
                        className="w-full"
                        size="large"
                    />
                );
            case "number":
                return (
                    <div className="flex items-center">
                        {prefix && <span className="mr-2 text-gray-500 text-xs">{prefix}</span>}
                        <Input
                            type="number"
                            value={value}
                            onChange={(e) => onChange(e.target.value)}
                            className="w-full"
                            max={max}
                        />
                        {suffix && <span className="ml-2 text-gray-500 text-xs">{suffix}</span>}
                    </div>
                );
            case "date":
                return (
                    <DatePicker
                        value={value ? dayjs(value) : null}
                        onChange={(date) => onChange(date ? date.format('YYYY-MM-DD') : '')}
                        className="w-full"
                        format="DD MMM YYYY"
                    />
                );
            default:
                return (
                    <Input
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        className="w-full"
                    />
                );
        }
    };

    return (
        <div className="flex flex-col">
            <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">{label}</span>
            {renderInput()}
        </div>
    );
};

export default CustomerDetails;